import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
    Wrench, User, Lock, LogIn, PlusCircle, UserCog, Search, Camera, CheckSquare, 
    ChevronRight, X, Info, Loader, CheckCircle, AlertCircle, Save, Plus, Trash2, 
    Printer, ImageOff, ExternalLink, Palette, Square, Circle, MousePointer, PenTool,
    Filter, ArrowUpDown, Eraser, Settings, Database
} from 'lucide-react';

console.log('BUILD: AHMShr-final-name-only-options-20260630-1700');

// ==========================================
// API & 伺服器設定區塊
// ==========================================
// 💡 除錯提示：
// 如果您想在 n8n 畫面點擊「Listen for test event」進行手動測試，
// 請將下方的網址改為 "https://goinfohr-n8n.zeabur.app/webhook-test" (加上 -test)
// 測試完成後記得改回正式版並將 n8n Workflow 設為 Active！
const API_BASE_URL = "https://goinfohr-n8n.zeabur.app/webhook"; 

const API = {
  LOGIN: `${API_BASE_URL}/login`,
  GET_INIT_DATA: `${API_BASE_URL}/get-init-data`,
  GET_REPAIR_DATA: `${API_BASE_URL}/get-repair-data`,
  SUBMIT_REPAIR: `${API_BASE_URL}/submit-repair-data`,
  GET_PENDING_DISPATCH: `${API_BASE_URL}/get-pending-dispatch`,
  SUBMIT_DISPATCH: `${API_BASE_URL}/submit-dispatch-data`,
  SEARCH_CLIENT: `${API_BASE_URL}/search-client-repair`,
  SEARCH_CLIENT_OVERVIEW: `${API_BASE_URL}/search-repair-detail`,
  SEARCH_VENDOR: `${API_BASE_URL}/search-vendor-dispatch`,
  GET_REPAIR_DETAIL: `${API_BASE_URL}/get-repair-detail`,
  GET_DISPATCH_DETAIL: `${API_BASE_URL}/get-dispatch-detail`,
  UPDATE_REPAIR_STATUS: `${API_BASE_URL}/update-repair-status`,
  // ★ 新增後台設定的 API 端點
  UPDATE_SETTINGS: `${API_BASE_URL}/update-system-settings`,
  VERIFY_ADMIN_PASSWORD: `${API_BASE_URL}/verify-admin-password` 
};

const ADMIN_WEBHOOK_SECRET = 'AHMS_ADMIN_2026';

// ★ 照片上傳伺服器設定
const COMPANY_SERVER_URL = "https://favorable-ruby-logically.ngrok-free.dev"; 
const API_IMAGE_UPLOAD = `${COMPANY_SERVER_URL}/upload`;
const UPLOAD_PATH_PREFIX = "/public/data/uploads";

// ==========================================
// 輔助工具函式與常數
// ==========================================
const handleApiError = (err, action) => {
    console.error(`[API Error] ${action}:`, err);
    let msg = err.message;
    if (msg === 'Failed to fetch') {
        msg = '網路連線失敗！請檢查:\n1. n8n 服務是否正常運行\n2. Webhook 是否已發布\n3. 網址路徑是否正確\n4. 是否有跨域 (CORS) 問題';
    } else if (msg.includes('Unexpected end of JSON input')) {
        msg = '伺服器回傳了空白內容。請檢查 n8n 工作流是否中斷。';
    }
    alert(`❌ ${action} 失敗：\n${msg}`);
};

const parseN8nResponse = (data) => {
    if (!data) return [];
    let list = Array.isArray(data) ? data : (data.data || data.results || [data]);
    return list.map(item => item.json ? item.json : item);
};

const resolvePhotoUrl = (url) => {
    if (!url) return null;
    if (typeof url !== 'string') return null;
    if (url === 'NULL' || url === 'null') return null;
    if (url.startsWith('blob:') || url.startsWith('data:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;

    let normalizedPath = url.replace(/\\/g, '/');
    normalizedPath = normalizedPath.replace(/^(\.\/|\.\.\/)+/, '');
    
    if (normalizedPath.startsWith('/public') || normalizedPath.startsWith('public/')) {
        const pathWithSlash = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
        return `${COMPANY_SERVER_URL}${encodeURI(pathWithSlash)}`;
    }
    const uploadsIndex = normalizedPath.toLowerCase().indexOf('uploads/');
    if (uploadsIndex !== -1) {
        const relativePart = normalizedPath.substring(uploadsIndex + 8); 
        return `${COMPANY_SERVER_URL}${UPLOAD_PATH_PREFIX}/${encodeURI(relativePart)}`;
    }
    if (normalizedPath.includes('/') && !normalizedPath.startsWith('/')) {
        return `${COMPANY_SERVER_URL}${UPLOAD_PATH_PREFIX}/${encodeURI(normalizedPath)}`;
    }
    let filename = normalizedPath.split('/').pop();
    if (filename) {
        return `${COMPANY_SERVER_URL}${UPLOAD_PATH_PREFIX}/Temp/${encodeURIComponent(filename)}`;
    }
    return null;
};

// ★ 狀態對應常數與轉換函式 (解決顯示數字的問題)
const WFLG_MAP = { '0': '官方LINE', '1': '物管填單', '2': '電話', '3': '官網留言', '4': '自主檢驗', '5': '其他' };
const FLG_MAP = { '0': '設計', '1': '施工', '2': '建材', '3': '使用', '4': '天然', '5': '年限', '6': '其他' };

const normalizeCodeNameList = (list = []) => {
    if (!Array.isArray(list)) return [];
    return list.map((item, idx) => {
        const key = String(item?.Code ?? item?.code ?? item?.Sno ?? item?.sno ?? item?.UserId ?? item?.id ?? idx).trim();
        const name = String(item?.Name ?? item?.name ?? item?.Wname ?? '').trim();
        const sno = String(item?.Sno ?? item?.sno ?? key).trim();
        return {
            Code: key,
            Sno: sno,
            Name: name,
        };
    }).filter(item => item.Code !== '' || item.Name !== '' || item.Sno !== '');
};

const listToMap = (list = [], fallback = {}) => {
    const mapped = { ...fallback };
    normalizeCodeNameList(list).forEach(item => {
        if (item.Code !== '') mapped[item.Code] = item.Name || item.Code;
    });
    return mapped;
};

const getMappedText = (val, map) => {
    if (val == null || val === '') return '無';
    const str = String(val).trim();
    if (map[str]) return map[str];
    if (str.includes('-')) return str.split('-')[1];
    return str;
};

// ==========================================
// 圖片顯示與編輯元件
// ==========================================
const SecureImage = ({ src, alt, className, onClick, ...props }) => {
    const [objectUrl, setObjectUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const generatedUrlRef = useRef(null);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(false);

        if (!src) { setLoading(false); return; }
        if (src.startsWith('blob:') || src.startsWith('data:')) { setObjectUrl(src); setLoading(false); return; }

        const targetUrl = resolvePhotoUrl(src);
        if (!targetUrl) { setLoading(false); return; }

        if (targetUrl.includes('ngrok')) {
            fetch(targetUrl, { headers: { 'ngrok-skip-browser-warning': 'true' } })
            .then(res => { if (!res.ok) throw new Error('Network response was not ok'); return res.blob(); })
            .then(blob => {
                if (isMounted) {
                    const url = URL.createObjectURL(blob);
                    if (generatedUrlRef.current) URL.revokeObjectURL(generatedUrlRef.current);
                    generatedUrlRef.current = url; 
                    setObjectUrl(url);
                    setLoading(false);
                }
            })
            .catch(err => {
                console.warn("Secure fetch failed, fallback to src", err);
                if (isMounted) { setObjectUrl(targetUrl); setLoading(false); }
            });
        } else {
            setObjectUrl(targetUrl);
            setLoading(false);
        }

        return () => {
            isMounted = false;
            if (generatedUrlRef.current) { URL.revokeObjectURL(generatedUrlRef.current); generatedUrlRef.current = null; }
        };
    }, [src]);

    const handleWrapperClick = (e) => { if (onClick) onClick(objectUrl || src); };

    if (loading) return <div className={`flex flex-col items-center justify-center bg-gray-100 text-gray-400 cursor-pointer ${className}`} onClick={handleWrapperClick}><Loader className="w-6 h-6 animate-spin mb-1"/><span className="text-[10px]">載入中...</span></div>;
    if (error) return <div className={`flex flex-col items-center justify-center bg-gray-200 text-red-400 cursor-pointer ${className}`} onClick={handleWrapperClick} title="圖片載入失敗"><ImageOff className="w-8 h-8 mb-1"/></div>;

    return (
        <img src={objectUrl || src} alt={alt} className={className} onClick={handleWrapperClick} onError={(e) => { if (objectUrl && objectUrl !== src) { e.target.src = src; setObjectUrl(null); } else { setError(true); } }} {...props} />
    );
};

const ImageEditorModal = ({ isOpen, onClose, imageSrc, onSave }) => {
    const canvasRef = useRef(null);
    const [color, setColor] = useState('#FF0000');
    const [tool, setTool] = useState('pen'); 
    const [lineWidth, setLineWidth] = useState(3);
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [snapshot, setSnapshot] = useState(null); 

    useEffect(() => {
        if (isOpen && imageSrc && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = imageSrc;
            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
            };
        }
    }, [isOpen, imageSrc]);

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
    };

    const startDraw = (e) => {
        if (!canvasRef.current) return;
        setIsDrawing(true);
        const pos = getPos(e);
        setStartPos(pos);
        const ctx = canvasRef.current.getContext('2d');
        setSnapshot(ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height));
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    };

    const draw = (e) => {
        if (!isDrawing || !canvasRef.current) return;
        e.preventDefault(); 
        const pos = getPos(e);
        const ctx = canvasRef.current.getContext('2d');

        if (tool === 'pen') {
            ctx.lineTo(pos.x, pos.y);
            ctx.stroke();
        } else {
            if (snapshot) ctx.putImageData(snapshot, 0, 0);
            ctx.beginPath();
            ctx.strokeStyle = color;
            ctx.lineWidth = lineWidth;

            if (tool === 'rect') {
                ctx.strokeRect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y);
            } else if (tool === 'circle') {
                const radius = Math.sqrt(Math.pow(pos.x - startPos.x, 2) + Math.pow(pos.y - startPos.y, 2));
                ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
                ctx.stroke();
            } else if (tool === 'arrow') {
                ctx.moveTo(startPos.x, startPos.y);
                ctx.lineTo(pos.x, pos.y);
                ctx.stroke();
                const headLen = lineWidth * 5; 
                const angle = Math.atan2(pos.y - startPos.y, pos.x - startPos.x);
                ctx.beginPath();
                ctx.moveTo(pos.x, pos.y);
                ctx.lineTo(pos.x - headLen * Math.cos(angle - Math.PI / 6), pos.y - headLen * Math.sin(angle - Math.PI / 6));
                ctx.lineTo(pos.x - headLen * Math.cos(angle + Math.PI / 6), pos.y - headLen * Math.sin(angle + Math.PI / 6));
                ctx.lineTo(pos.x, pos.y);
                ctx.fillStyle = color;
                ctx.fill();
            }
        }
    };

    const stopDraw = () => setIsDrawing(false);

    const clearCanvas = () => {
        if (!canvasRef.current || !imageSrc) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = imageSrc;
        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
        };
    };

    const handleSave = () => {
        if (canvasRef.current) {
            const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.8);
            onSave(dataUrl);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-[10001] flex flex-col items-center justify-center p-2">
             <div className="w-full max-w-4xl bg-gray-900 rounded-lg overflow-hidden flex flex-col h-[90vh]">
                 <div className="bg-gray-800 p-2 flex flex-wrap gap-3 items-center justify-between shrink-0">
                     <div className="flex gap-2">
                         <button onClick={() => setTool('pen')} className={`p-2 rounded ${tool === 'pen' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}><PenTool size={20} /></button>
                         <button onClick={() => setTool('rect')} className={`p-2 rounded ${tool === 'rect' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}><Square size={20} /></button>
                         <button onClick={() => setTool('circle')} className={`p-2 rounded ${tool === 'circle' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}><Circle size={20} /></button>
                         <button onClick={() => setTool('arrow')} className={`p-2 rounded ${tool === 'arrow' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}><MousePointer size={20} className="rotate-[-45deg]"/></button>
                     </div>
                     <div className="flex gap-2 items-center">
                         {['#FF0000', '#FFFF00', '#0000FF'].map(c => (
                             <button key={c} onClick={() => setColor(c)} className={`w-8 h-8 rounded-full border-2 ${color === c ? 'border-white scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />
                         ))}
                     </div>
                     <div className="flex gap-2 items-center">
                          <input type="range" min="1" max="10" value={lineWidth} onChange={(e) => setLineWidth(parseInt(e.target.value))} className="w-20" />
                     </div>
                     <div className="flex gap-2">
                         <button onClick={clearCanvas} className="px-4 py-2 bg-yellow-600 text-white rounded font-bold hover:bg-yellow-700 flex items-center justify-center border border-yellow-500 shadow-md">清除</button>
                         <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded font-bold hover:bg-gray-700 border border-gray-500">取消</button>
                         <button onClick={handleSave} className="px-4 py-2 bg-green-600 text-white rounded font-bold hover:bg-green-700 flex items-center gap-2 border border-green-500 shadow-md"><Save size={16}/> 儲存</button>
                     </div>
                 </div>
                 <div className="flex-1 overflow-auto flex items-center justify-center bg-black relative touch-none">
                     <canvas 
                        ref={canvasRef}
                        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                        className="max-w-full max-h-full object-contain"
                     />
                 </div>
             </div>
        </div>
    );
};

// ★ 電子簽名板元件
const SignaturePad = React.forwardRef((props, ref) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const rect = canvas.parentElement.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = 120; 
            const ctx = canvas.getContext('2d');
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        }
    }, []);

    React.useImperativeHandle(ref, () => ({
        getSignatureImage: () => {
            if (!hasDrawn) return null;
            return canvasRef.current.toDataURL('image/png');
        },
        clear: () => clearSignature()
    }));

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    const startDraw = (e) => {
        e.preventDefault(); 
        setIsDrawing(true);
        const pos = getPos(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        e.preventDefault(); 
        const pos = getPos(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        if (!hasDrawn) setHasDrawn(true);
    };

    const stopDraw = () => {
        setIsDrawing(false);
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasDrawn(false);
    };

    return (
        <div className="relative w-full border border-gray-300 rounded-lg bg-white overflow-hidden group">
            <canvas
                ref={canvasRef}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                style={{ touchAction: 'none' }} 
                className="w-full cursor-crosshair"
            />
            {hasDrawn && (
                <button 
                    onClick={clearSignature}
                    className="absolute top-1 right-1 bg-gray-100 hover:bg-gray-200 text-gray-600 p-1 rounded text-xs flex items-center gap-1 opacity-80 hover:opacity-100 transition-opacity print:hidden"
                    title="清除重簽"
                >
                    <Eraser size={14} /> 清除
                </button>
            )}
            <div className="absolute inset-0 pointer-events-none border-b-2 border-dashed border-gray-200 top-auto bottom-8 mx-4 opacity-50 print:opacity-30"></div>
        </div>
    );
});


// ==========================================
// 共用元件：廠商與建案選擇
// ==========================================
function ContractorSelectModal({ isOpen, onClose, onSelect, contractors }) {
    const [search, setSearch] = useState('');
    if (!isOpen) return null;
    
    const filtered = contractors.filter(c => 
        (c.Name || c.name || '').includes(search) || 
        (c.Prdtcode || c.prdtcode || c.Code || c.code || '').includes(search)
    );

    return (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200 print:hidden">
            <div className="bg-white w-full max-w-md rounded-xl shadow-xl flex flex-col h-[70vh] overflow-hidden">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                    <h3 className="font-bold text-lg text-gray-800">選擇施作廠商</h3>
                    <button onClick={onClose} className="hover:bg-gray-200 p-1 rounded-full transition-colors"><X className="w-5 h-5 text-gray-500"/></button>
                </div>
                <div className="p-3 border-b bg-white">
                    <div className="relative">
                        <input 
                            type="text" 
                            placeholder="搜尋廠商代號或名稱關鍵字..." 
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold" 
                            value={search} onChange={e => setSearch(e.target.value)} autoFocus 
                        />
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3"/>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto bg-gray-50">
                    {filtered.length === 0 ? (
                        <div className="text-center py-10 text-gray-400 font-bold">查無廠商資料</div>
                    ) : (
                        filtered.map((c, idx) => (
                            <button 
                                key={idx} onClick={() => { onSelect(c); setSearch(''); }} 
                                className="w-full text-left px-4 py-3 bg-white hover:bg-blue-50 border-b border-gray-100 grid grid-cols-[100px_1fr] gap-4 items-center transition-colors focus:bg-blue-100"
                            >
                                <span className="text-sm text-blue-700 font-bold font-mono">{c.Prdtcode || c.prdtcode || c.Code || c.code}</span>
                                <span className="font-bold text-gray-800 text-base">{c.Name || c.name}</span>
                            </button>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

function ProjectSelectModal({ isOpen, onClose, projects, selectedProjects, setSelectedProjects }) {
    if (!isOpen) return null;

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedProjects(projects.map(p => String(p.Wcode || p.wcode)));
        else setSelectedProjects([]);
    };

    const toggleProject = (val) => {
        if (selectedProjects.includes(val)) setSelectedProjects(selectedProjects.filter(id => id !== val));
        else setSelectedProjects([...selectedProjects, val]);
    };

    const allSelected = projects.length > 0 && selectedProjects.length === projects.length;

    return (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200 print:hidden">
            <div className="bg-white w-full max-w-md rounded-xl shadow-xl flex flex-col h-[70vh] overflow-hidden">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                    <h3 className="font-bold text-lg text-gray-800">選擇建案 (可多選)</h3>
                    <button onClick={onClose} className="hover:bg-gray-200 p-1 rounded-full transition-colors"><X className="w-5 h-5 text-gray-500"/></button>
                </div>
                <div className="grid grid-cols-[40px_80px_1fr] gap-4 items-center px-4 py-2 bg-gray-200 font-bold text-gray-700 text-sm border-b shadow-inner">
                    <div className="text-center"><input type="checkbox" checked={allSelected} onChange={handleSelectAll} className="w-4 h-4 rounded text-blue-600 cursor-pointer" /></div>
                    <span>建案代號</span><span className="text-left">建案名稱</span>
                </div>
                <div className="flex-1 overflow-y-auto bg-gray-50">
                    {projects.map((p, idx) => {
                        const val = String(p.Wcode || p.wcode);
                        const isChecked = selectedProjects.includes(val);
                        return (
                            <label key={idx} className={`w-full px-4 py-3 bg-white hover:bg-blue-50 border-b border-gray-100 grid grid-cols-[40px_80px_1fr] gap-4 items-center transition-colors cursor-pointer ${isChecked ? 'bg-blue-50' : ''}`}>
                                <div className="text-center"><input type="checkbox" checked={isChecked} onChange={() => toggleProject(val)} className="w-4 h-4 rounded text-blue-600 cursor-pointer" /></div>
                                <span className="text-sm text-blue-700 font-bold font-mono">{p.Wcode || p.wcode}</span>
                                <span className="font-bold text-gray-800 text-base">{p.Wname || p.wname || p.Name}</span>
                            </label>
                        )
                    })}
                </div>
                <div className="p-4 bg-white border-t flex justify-end">
                    <button onClick={onClose} className="bg-blue-600 text-white px-8 py-2 rounded-lg font-bold shadow-md hover:bg-blue-700 active:scale-95 transition-all">確定</button>
                </div>
            </div>
        </div>
    );
}

// ==========================================
// 主應用程式元件
// ==========================================
export default function BuildingRepairApp() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [adminPwdInput, setAdminPwdInput] = useState('');
  const [adminAuthLoading, setAdminAuthLoading] = useState(false); 
  const [currentView, setCurrentView] = useState('addRepair'); 
  const isBackendStandalone = currentView === 'backend';
  
  const [globalData, setGlobalData] = useState({ 
      projects: [], contractors: [], categories: [], items: [], persons: [], fixMethods: [], reasons: [] 
  });
  const [globalLoading, setGlobalLoading] = useState(true);
  const [globalError, setGlobalError] = useState(null); 

  const fixMethodMap = listToMap(globalData.fixMethods, WFLG_MAP);
  const reasonMap = listToMap(globalData.reasons, FLG_MAP);
  const fixMethodOptions = normalizeCodeNameList(globalData.fixMethods || []).length > 0
      ? normalizeCodeNameList(globalData.fixMethods || [])
      : normalizeCodeNameList(Object.entries(WFLG_MAP).map(([Code, Name]) => ({ Code, Name })));
  const reasonOptions = normalizeCodeNameList(globalData.reasons || []).length > 0
      ? normalizeCodeNameList(globalData.reasons || [])
      : normalizeCodeNameList(Object.entries(FLG_MAP).map(([Code, Name]) => ({ Code, Name })));

// 🌟 修改這裡的 useEffect (SSO登入與重新整理判斷)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const usernameFromUrl = urlParams.get('username');
    const nameFromUrl = urlParams.get('name') || usernameFromUrl; 

    if (tokenFromUrl) {
      // (1) 從入口網站跳轉過來，寫入登入狀態
      localStorage.setItem('portalToken', tokenFromUrl); 
      if (usernameFromUrl) {
        localStorage.setItem('portalUser', JSON.stringify({ displayName: nameFromUrl, username: usernameFromUrl }));
      }
      setIsLoggedIn(true); 
      setUserInfo({ account: usernameFromUrl, name: nameFromUrl });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // (2) 🌟 新增：處理重新整理 (F5) 的情況
      // 如果網址沒有 token，嘗試從 localStorage 讀取已存在的登入狀態
      const savedToken = localStorage.getItem('portalToken');
      const savedUserStr = localStorage.getItem('portalUser');
      if (savedToken && savedUserStr) {
        try {
          const savedUser = JSON.parse(savedUserStr);
          setIsLoggedIn(true);
          setUserInfo({ account: savedUser.username, name: savedUser.displayName });
        } catch(e) {}
      }
    }
  }, []);
    
  useEffect(() => {
    document.querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']").forEach(e => e.remove());

    const addIcon = (rel, size, href) => {
      const link = document.createElement('link');
      link.rel = rel;
      if (size) link.sizes = size;
      link.href = href;
      document.head.appendChild(link);
    };

    const logoUrl = 'ahmsbr_logo.png'; 

    addIcon('icon', '192x192', logoUrl);
    addIcon('icon', '512x512', logoUrl);
    addIcon('icon', '32x32', logoUrl);
    
    addIcon('apple-touch-icon', '180x180', logoUrl);
    addIcon('apple-touch-icon', '152x152', logoUrl);
    addIcon('apple-touch-icon', '', logoUrl);

    if (!document.querySelector("meta[name='apple-mobile-web-app-capable']")) {
        const metaApple = document.createElement('meta');
        metaApple.name = 'apple-mobile-web-app-capable';
        metaApple.content = 'yes';
        document.head.appendChild(metaApple);
    }
    if (!document.querySelector("meta[name='mobile-web-app-capable']")) {
        const metaMobile = document.createElement('meta');
        metaMobile.name = 'mobile-web-app-capable';
        metaMobile.content = 'yes';
        document.head.appendChild(metaMobile);
    }

    document.title = "AHMShr房屋報修管理系統";
  }, []);

  const fetchGlobalData = async () => {
      setGlobalLoading(true);
      setGlobalError(null);
      try {
          const res = await fetch(API.GET_INIT_DATA, { 
              method: 'POST', 
              headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
              body: JSON.stringify({ action: 'init', account: userInfo?.account || '' }) 
          });
          
          if (!res.ok) throw new Error(`伺服器回應錯誤代碼: ${res.status}`);
          
          const textData = await res.text();
          if (!textData || textData.trim() === '') throw new Error('伺服器回傳了空白內容，請檢查 n8n 的 get-init-data 工作流');
          
          const rawData = JSON.parse(textData);
          
          let data = Array.isArray(rawData) ? rawData[0] : rawData;
          if (data && data.json) data = data.json;
          
          const sqlJsonKey = data ? Object.keys(data).find(k => k.startsWith('JSON_')) : null;
          if (sqlJsonKey) {
              try {
                  const parsedRoot = JSON.parse(data[sqlJsonKey]);
                  data = Array.isArray(parsedRoot) ? parsedRoot[0] : parsedRoot;
              } catch (e) {
                  console.warn("SQL Root JSON 拆解失敗:", e);
              }
          }
          
          const parseField = (field) => {
              if (!field) return [];
              if (typeof field === 'string') {
                  try { return JSON.parse(field); } catch(e) { return []; }
              }
              return Array.isArray(field) ? field : [];
          };

          const parsedData = {
              projects: parseField(data?.projects || data?.Projects),
              contractors: parseField(data?.contractors || data?.Contractors),
              categories: parseField(data?.categories || data?.Categories),
              items: parseField(data?.items || data?.Items),
              persons: parseField(data?.persons || data?.Persons),
              fixMethods: parseField(data?.fixMethods || data?.FixMethods),
              reasons: parseField(data?.reasons || data?.Reasons)
          };

          if (parsedData.projects.length === 0 && parsedData.contractors.length === 0) {
              throw new Error("API 連線成功，但解析不到資料！請檢查 n8n SQL 語法是否有正確回傳 projects, contractors 等欄位。");
          }

          setGlobalData(parsedData);

          let currentName = data?.currentUserName || data?.CurrentUserName;
          
          if (typeof currentName === 'string' && currentName.trim().startsWith('[')) {
              try { 
                  const parsed = JSON.parse(currentName);
                  currentName = parsed[0]?.Name || parsed[0]?.name || currentName; 
              } catch(e) {}
          } else if (Array.isArray(currentName)) {
              currentName = currentName[0]?.Name || currentName[0]?.name || currentName;
          }

          if (currentName && typeof currentName === 'string' && currentName !== userInfo.name) {
              setUserInfo(prev => ({ ...prev, name: currentName }));
          }

      } catch (err) {
          console.error('[API Error] 載入基礎資料失敗:', err);
          setGlobalError(err.message === 'Failed to fetch' 
              ? '無法連線到 n8n (CORS阻擋)，請確認「get-init-data」工作流已發布，且 Webhook 節點有啟用 Respond to preflight (OPTIONS)。' 
              : err.message);
      } finally {
          setGlobalLoading(false);
      }
  };

  useEffect(() => {
      if(isLoggedIn) fetchGlobalData();
  }, [isLoggedIn]);

// ★ Header 右鍵點擊後，開啟與 AHMShi 一樣風格的管理員驗證視窗
const handleHeaderContextMenu = (e) => {
    e.preventDefault();
    setAdminPwdInput('');
    setShowAdminAuth(true);
};

const verifyAdminPassword = async () => {
    if (!adminPwdInput.trim()) {
        alert('請輸入 ROOT 密碼！');
        return;
    }

    try {
        setAdminAuthLoading(true);

        const response = await fetch(API.VERIFY_ADMIN_PASSWORD, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': 'true',
                'X-Webhook-Secret': ADMIN_WEBHOOK_SECRET
            },
            body: JSON.stringify({
                password: adminPwdInput
            })
        });

        const rawText = await response.text();
        let result = null;

        try {
            result = rawText ? JSON.parse(rawText) : null;
        } catch (err) {
            result = { ok: false, message: rawText || '驗證回應格式錯誤' };
        }

        if (response.ok && result?.ok) {
            setShowAdminAuth(false);
            setAdminPwdInput('');
            setCurrentView('backend');
        } else {
            alert(result?.message || '密碼錯誤，無法進入後台！');
            setAdminPwdInput('');
        }
    } catch (err) {
        console.error('verify admin password failed:', err);
        alert('管理員驗證失敗，請稍後再試！');
    } finally {
        setAdminAuthLoading(false);
    }
};

  if (!isLoggedIn) {
    return <LoginScreen onLogin={(user) => { setIsLoggedIn(true); setUserInfo(user); }} />;
  }

  return (
    <div className={isBackendStandalone ? "min-h-screen bg-[#eef3fb] font-sans print:block print:min-h-0 print:bg-white" : "min-h-screen bg-gray-50 flex flex-col font-sans print:block print:min-h-0 print:bg-white"}>
      {/* 在 Header 綁定右鍵事件 onContextMenu */}
      {!isBackendStandalone && <header 
          onContextMenu={handleHeaderContextMenu} 
          className="bg-blue-600 text-white pt-8 pb-4 px-4 flex flex-col items-center relative shadow-sm z-10 flex-shrink-0 print:hidden select-none cursor-default"
          title="提示：在藍色區域點擊右鍵可進入隱藏後台"
      >
        <div className="flex items-center gap-4 mb-3">
          <div className="border-2 border-white rounded-xl p-2 flex items-center justify-center">
            <Wrench className="w-8 h-8 text-white" />
          </div>
          <div className="flex flex-col items-start text-left">
            <div className="text-3xl font-bold tracking-widest leading-none mb-2">AHMShr</div>
            <div className="text-base font-bold tracking-widest leading-none">房屋報修管理系統</div>
          </div>
        </div>
        <div className="text-blue-100 text-sm tracking-widest mt-1">簡單 • 標準 • 專業</div>
        
        <div className="absolute bottom-4 right-6 flex items-center justify-end gap-2 text-sm font-bold text-blue-100 text-right">
          <User className="w-4 h-4"/>
          <span>{userInfo?.name}</span>
          <span className="text-blue-300 mx-1">|</span>
          <button 
            onClick={() => {
              if (window.confirm("確定要返回系統選擇清單嗎？")) {
                window.location.href = 'https://goinfo-portal.zeabur.app'; 
              }
            }} 
            className="hover:text-white transition-colors cursor-pointer underline-offset-2 hover:underline"
          >
            返回系統清單
          </button>        
        </div>
      </header>}

      {showAdminAuth && (
        <div className="fixed inset-0 z-[2000] bg-[rgba(68,72,80,0.72)] backdrop-blur-[1px] flex items-end sm:items-center justify-center px-3 sm:px-4 pb-0 sm:pb-0">
          <div className="w-full max-w-[402px] rounded-t-2xl sm:rounded-2xl bg-white shadow-[0_24px_70px_rgba(15,23,42,0.30)] px-6 pt-7 pb-5 sm:pb-6">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-[66px] w-[66px] items-center justify-center rounded-full bg-white text-slate-600">
                <Settings className="h-10 w-10 text-slate-600" strokeWidth={2.15} />
              </div>

              <h2 className="mt-1 mb-6 text-[17px] font-extrabold tracking-[0.08em] text-slate-900">
                系統管理員身分驗證
              </h2>

              <input
                type="password"
                value={adminPwdInput}
                onChange={(e) => setAdminPwdInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !adminAuthLoading) verifyAdminPassword();
                  if (e.key === 'Escape') {
                    setShowAdminAuth(false);
                    setAdminPwdInput('');
                  }
                }}
                className="h-[56px] w-full rounded-[10px] border-2 border-[#5d94ea] px-4 text-center text-[16px] tracking-[0.22em] text-slate-700 placeholder:text-slate-400 focus:border-[#4b84df] focus:outline-none focus:ring-[3px] focus:ring-blue-100"
                placeholder="請輸入 ROOT 密碼"
                autoFocus
              />

              <div className="mt-4 grid w-full grid-cols-[0.96fr_1.04fr] gap-3">
                <button
                  onClick={() => {
                    setShowAdminAuth(false);
                    setAdminPwdInput('');
                  }}
                  className="h-[48px] rounded-[8px] bg-[#f1f3f7] text-[16px] font-bold text-slate-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition hover:bg-[#e9edf3]"
                  disabled={adminAuthLoading}
                >
                  取消
                </button>

                <button
                  onClick={verifyAdminPassword}
                  className="h-[48px] rounded-[8px] bg-[#1f2f49] text-[16px] font-bold text-white shadow-[0_8px_18px_rgba(31,47,73,0.22)] transition hover:bg-[#1a2740] disabled:opacity-60"
                  disabled={adminAuthLoading}
                >
                  {adminAuthLoading ? '驗證中...' : '確認登入'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isBackendStandalone && (
        <div className="flex bg-white border-b border-gray-200 shadow-sm z-10 sticky top-0 flex-shrink-0 print:hidden overflow-x-auto">
          <button onClick={() => setCurrentView('addRepair')} className={`flex-1 py-3 px-4 min-w-[100px] text-sm font-bold text-center border-b-2 transition-colors ${currentView === 'addRepair' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-blue-500'}`}>
            新增報修
          </button>
          <button onClick={() => setCurrentView('dispatch')} className={`flex-1 py-3 px-4 min-w-[120px] text-sm font-bold text-center border-b-2 transition-colors ${currentView === 'dispatch' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-blue-500'}`}>
            廠商派工修繕
          </button>
          <button onClick={() => setCurrentView('tracking')} className={`flex-1 py-3 px-4 min-w-[120px] text-sm font-bold text-center border-b-2 transition-colors ${currentView === 'tracking' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-blue-500'}`}>
            報修追蹤查詢
          </button>
        </div>
      )}

      <main className={currentView === "backend" ? "flex-1 w-full overflow-y-auto relative print:static print:overflow-visible print:max-w-none print:p-0 print:m-0" : "flex-1 p-4 w-full max-w-4xl mx-auto overflow-y-auto relative print:static print:overflow-visible print:max-w-none print:p-0 print:m-0"}>
        {globalLoading && (
            <div className="absolute inset-0 bg-white/80 z-50 flex flex-col items-center justify-center print:hidden">
                <Loader className="w-10 h-10 animate-spin text-blue-600 mb-2"/>
                <p className="text-gray-600 font-bold mt-3">載入資料庫基礎資料中...</p>
            </div>
        )}
        
        {globalError && !globalLoading && (
            <div className="absolute inset-0 bg-white z-40 flex flex-col items-center justify-center p-6 text-center print:hidden">
                <AlertCircle className="w-16 h-16 text-red-500 mb-4"/>
                <h2 className="text-xl font-bold text-gray-800 mb-2">載入基礎資料失敗</h2>
                <p className="text-gray-600 mb-6 bg-red-50 p-4 rounded-lg border border-red-100">{globalError}</p>
                <button onClick={fetchGlobalData} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold shadow-lg hover:bg-blue-700 active:scale-95 transition-all flex items-center gap-2">
                    重新連線嘗試
                </button>
            </div>
        )}

        {!globalLoading && !globalError && (
            <>
                {currentView === 'addRepair' && <AddRepairScreen globalData={globalData} userInfo={userInfo} fixMethodOptions={fixMethodOptions} reasonOptions={reasonOptions} />}
                {currentView === 'dispatch' && <DispatchScreen globalData={globalData} userInfo={userInfo} />}
                {currentView === 'tracking' && <TrackingScreen globalData={globalData} userInfo={userInfo} />}
                {/* ★ 渲染後台設定畫面 */}
                {currentView === 'backend' && <BackendScreen globalData={globalData} setGlobalData={setGlobalData} setCurrentView={setCurrentView} />}
            </>
        )}
      </main>
    </div>
  );
}

// ==========================================
// ★ 新增：修繕項目設定 (後台畫面)
// ==========================================
function BackendScreen({ globalData, setGlobalData, setCurrentView }) {
    const [categories, setCategories] = useState(globalData.categories || []);
    const [items, setItems] = useState(globalData.items || []);
    const [fixMethods, setFixMethods] = useState(normalizeCodeNameList(globalData.fixMethods || []));
    const [reasons, setReasons] = useState(normalizeCodeNameList(globalData.reasons || []));
    const [persons, setPersons] = useState(normalizeCodeNameList(globalData.persons || []));
    const [activeTab, setActiveTab] = useState('categories');
    const [selectedCategoryCode, setSelectedCategoryCode] = useState(null);
    const [saving, setSaving] = useState(false);

    const [newCategory, setNewCategory] = useState({ Code: '', Name: '' });
    const [newItem, setNewItem] = useState({ Sno: '', Name: '' });
    const [newFixMethod, setNewFixMethod] = useState({ Code: '0', Name: '' });
    const [newReason, setNewReason] = useState({ Code: '0', Name: '' });
    const [newPerson, setNewPerson] = useState({ Sno: '0', Name: '' });

    useEffect(() => {
        setCategories(globalData.categories || []);
    }, [globalData.categories]);

    useEffect(() => {
        setItems(globalData.items || []);
    }, [globalData.items]);

    useEffect(() => {
        const normalized = resequenceSimpleList(globalData.fixMethods || []);
        setFixMethods(normalized);
        setNewFixMethod(prev => ({ ...prev, Code: getNextSimpleCode(normalized) }));
    }, [globalData.fixMethods]);

    useEffect(() => {
        const normalized = resequenceSimpleList(globalData.reasons || []);
        setReasons(normalized);
        setNewReason(prev => ({ ...prev, Code: getNextSimpleCode(normalized) }));
    }, [globalData.reasons]);

    useEffect(() => {
        const normalized = resequencePersonsList(globalData.persons || []);
        setPersons(normalized);
        setNewPerson(prev => ({ ...prev, Sno: getNextSimpleCode(normalized) }));
    }, [globalData.persons]);

    const syncToGlobal = (next = {}) => {
        setGlobalData(prev => ({
            ...prev,
            categories: next.categories ?? categories,
            items: next.items ?? items,
            fixMethods: next.fixMethods ?? fixMethods,
            reasons: next.reasons ?? reasons,
            persons: next.persons ?? persons,
        }));
    };

    const handleAddCategory = () => {
        if (!newCategory.Code.trim() || !newCategory.Name.trim()) return alert('請填寫完整項目代碼與名稱！');
        if (categories.some(c => String(c.Code || c.code) === newCategory.Code.trim())) return alert('項目代碼已存在！');
        const updated = [...categories, { Code: newCategory.Code.trim(), Name: newCategory.Name.trim() }];
        setCategories(updated);
        syncToGlobal({ categories: updated });
        setNewCategory({ Code: '', Name: '' });
        if (!selectedCategoryCode) setSelectedCategoryCode(updated[0]?.Code || null);
    };

    const handleDeleteCategory = (codeToDelete) => {
        if (!window.confirm(`確定刪除代碼 ${codeToDelete} 及其所有關聯的細項嗎？`)) return;
        const updatedCategories = categories.filter(c => String(c.Code || c.code) !== String(codeToDelete));
        const updatedItems = items.filter(i => String(i.Pcode || i.pcode) !== String(codeToDelete));
        setCategories(updatedCategories);
        setItems(updatedItems);
        syncToGlobal({ categories: updatedCategories, items: updatedItems });
        if (String(selectedCategoryCode) === String(codeToDelete)) setSelectedCategoryCode(updatedCategories[0]?.Code || null);
    };

    const handleAddItem = () => {
        if (!selectedCategoryCode) return alert('請先選擇一個修繕項目！');
        if (!newItem.Sno.trim() || !newItem.Name.trim()) return alert('請填寫完整序碼與名稱！');
        if (items.some(i => String(i.Pcode || i.pcode) === String(selectedCategoryCode) && String(i.Sno || i.sno) === String(newItem.Sno.trim()))) return alert('此序碼已存在！');
        const updatedItems = [...items, { Pcode: selectedCategoryCode, Sno: newItem.Sno.trim(), Name: newItem.Name.trim() }];
        setItems(updatedItems);
        syncToGlobal({ items: updatedItems });
        setNewItem({ Sno: '', Name: '' });
    };

    const handleDeleteItem = (snoToDelete) => {
        if (!window.confirm('確定刪除此問題細項？')) return;
        const updatedItems = items.filter(i => !(String(i.Pcode || i.pcode) === String(selectedCategoryCode) && String(i.Sno || i.sno) === String(snoToDelete)));
        setItems(updatedItems);
        syncToGlobal({ items: updatedItems });
    };

    const handleChangeSimple = (setter, list, idx, field, value, typeKey) => {
        const updated = list.map((row, i) => i === idx ? { ...row, [field]: value } : row);
        setter(updated);
        syncToGlobal({ [typeKey]: updated });
    };

    const handleChangeCategory = (idx, field, value) => {
        const currentCode = categories[idx]?.Code || categories[idx]?.code;
        const updated = categories.map((row, i) => i === idx ? { ...row, [field]: value } : row);
        setCategories(updated);
        syncToGlobal({ categories: updated });
        if (field === 'Code' && String(selectedCategoryCode) === String(currentCode)) setSelectedCategoryCode(value);
    };

    const handleChangeItem = (idx, field, value) => {
        const current = currentItems[idx];
        const updated = items.map(row => {
            const isTarget = String(row.Pcode || row.pcode) === String(selectedCategoryCode) && String(row.Sno || row.sno) === String(current?.Sno || current?.sno);
            return isTarget ? { ...row, [field]: value } : row;
        });
        setItems(updated);
        syncToGlobal({ items: updated });
    };

    const handleAddSimple = (type, list, setter, value, emptySetter) => {
        const Name = String(value.Name || '').trim();
        if (!Name) return alert('請填寫名稱！');
        const Key = getNextSimpleCode(list);
        const updated = resequenceSimpleList([...list, { Code: Key, Name }]);
        setter(updated);
        syncToGlobal({ [type]: updated });
        emptySetter({ Code: getNextSimpleCode(updated), Name: '' });
    };

    const handleDeleteSimple = (type, list, setter, code) => {
        if (!window.confirm(`確定刪除代碼 ${code} 嗎？`)) return;
        const updated = resequenceSimpleList(list.filter(row => String(row.Code) !== String(code)));
        setter(updated);
        syncToGlobal({ [type]: updated });
    };

    const handleAddPerson = () => {
        const Name = String(newPerson.Name || '').trim();
        if (!Name) return alert('請填寫姓名！');
        const Sno = getNextSimpleCode(persons);
        const updated = resequencePersonsList([...persons, { Code: Sno, Sno, Name }]);
        setPersons(updated);
        syncToGlobal({ persons: updated });
        setNewPerson({ Sno: getNextSimpleCode(updated), Name: '' });
    };

    const handleChangePerson = (idx, field, value) => {
        const updated = persons.map((row, i) => i === idx ? { ...row, [field]: value } : row);
        const normalized = resequencePersonsList(updated);
        setPersons(normalized);
        syncToGlobal({ persons: normalized });
    };

    const handleDeletePerson = (sno) => {
        if (!window.confirm(`確定刪除序號 ${sno} 嗎？`)) return;
        const updated = resequencePersonsList(persons.filter(row => String(row.Sno || row.Code) !== String(sno)));
        setPersons(updated);
        syncToGlobal({ persons: updated });
    };

    const handleSaveToDatabase = async () => {
        setSaving(true);
        try {
            const res = await fetch(API.UPDATE_SETTINGS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'ngrok-skip-browser-warning': 'true',
                    'X-Webhook-Secret': ADMIN_WEBHOOK_SECRET
                },
                body: JSON.stringify({ categories, items, fixMethods, reasons, persons })
            });
            const textData = await res.text();
            let result = null;
            try { result = textData ? JSON.parse(textData) : null; } catch(e) { result = null; }
            if (!res.ok || result?.ok === false) throw new Error(result?.message || textData || `伺服器回應 ${res.status}`);
            alert('✅ 資料庫設定已更新成功！');
        } catch (err) {
            handleApiError(err, '儲存系統設定');
        } finally {
            setSaving(false);
        }
    };

    const currentItems = items.filter(i => String(i.Pcode || i.pcode) === String(selectedCategoryCode));
    const selectedCatData = categories.find(c => String(c.Code || c.code) === String(selectedCategoryCode));

    const getNextSimpleCode = (list = []) => {
        const used = normalizeCodeNameList(list)
            .map(row => Number.parseInt(String(row.Code ?? row.Sno ?? '').trim(), 10))
            .filter(Number.isInteger);
        let next = 0;
        while (used.includes(next)) next += 1;
        return String(next);
    };

    const resequenceSimpleList = (list = []) => normalizeCodeNameList(list).map((row, idx) => ({
        ...row,
        Code: String(idx),
    }));

    const resequencePersonsList = (list = []) => normalizeCodeNameList(list).map((row, idx) => ({
        ...row,
        Code: String(idx),
        Sno: String(idx),
    }));

    useEffect(() => {
        if (!selectedCategoryCode && categories.length > 0) setSelectedCategoryCode(categories[0].Code || categories[0].code);
    }, [categories, selectedCategoryCode]);

    const tabClass = (tab) => `px-5 py-3 rounded-xl font-bold border transition-all shadow-sm ${activeTab === tab ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`;

    const renderSimpleMaster = (title, tableName, list, setter, newValue, setNewValue, typeKey, placeholderName = '輸入名稱') => (
        <div className="grid grid-cols-1 xl:grid-cols-[340px,minmax(0,1fr)] gap-4 w-full">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-slate-100 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h3 className="font-black text-slate-800">{title}</h3>
                        {tableName ? <p className="text-xs text-slate-500 font-bold mt-1">{tableName}</p> : null}
                    </div>
                    <span className="text-xs bg-white border border-gray-200 px-2 py-1 rounded-lg font-bold text-slate-600">{list.length} 筆</span>
                </div>
                <div className="p-3 space-y-2 border-b border-gray-100 bg-gray-50/70">
                    <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-700 bg-white" placeholder={placeholderName} value={newValue.Name} onChange={e => setNewValue(prev => ({ ...prev, Name: e.target.value }))} />
                    <button onClick={() => handleAddSimple(typeKey, list, setter, newValue, setNewValue)} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2.5 font-bold flex items-center justify-center gap-2 shadow-sm"><PlusCircle className="w-4 h-4"/>新增資料</button>
                </div>
                <div className="max-h-[620px] overflow-y-auto">
                    {list.length === 0 ? (
                        <div className="px-4 py-10 text-center text-slate-400 font-bold">目前尚無資料</div>
                    ) : list.map((row, idx) => (
                        <div key={`${typeKey}-${row.Code}-${idx}`} className="px-4 py-3 border-b border-gray-100 bg-white hover:bg-blue-50/40">
                            <div className="flex items-center gap-2 mb-2">
                                <input value={row.Code} readOnly className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-mono text-slate-500 bg-slate-50 cursor-not-allowed" />
                                <button onClick={() => handleDeleteSimple(typeKey, list, setter, row.Code)} className="ml-auto w-8 h-8 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100 flex items-center justify-center"><Trash2 className="w-4 h-4"/></button>
                            </div>
                            <input value={row.Name} onChange={e => handleChangeSimple(setter, list, idx, 'Name', e.target.value, typeKey)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 bg-white" />
                        </div>
                    ))}
                </div>
            </div>
            <div className="bg-white border border-dashed border-gray-200 rounded-2xl shadow-sm flex items-center justify-center min-h-[220px] text-center p-8">
                <div>
                    <div className="text-lg font-black text-slate-700 mb-2">{title}</div>
                    <div className="text-sm text-slate-500 font-bold leading-7">左側清單可直接編輯與刪除，<br/>完成後按右上角「儲存設定」即可寫入資料庫。</div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#eef3fb] w-full">
            <div className="bg-slate-800 text-white px-4 sm:px-6 py-4 shadow-md">
                <div className="w-full px-4 sm:px-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-400/20 flex items-center justify-center">
                            <Database className="w-5 h-5 text-blue-300"/>
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-2xl font-black tracking-wide">AHMShr 參數設定</h2>
                                <span className="text-[11px] px-2 py-1 rounded-md bg-blue-600/40 border border-blue-400/20 text-blue-100 font-black tracking-widest">ROOT ADMIN</span>
                            </div>
                            
                        </div>
                    </div>
                    <div className="flex items-center gap-2 self-end lg:self-auto">
                        <button onClick={handleSaveToDatabase} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-black flex items-center gap-2 shadow-sm disabled:opacity-60">
                            {saving ? <Loader className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                            儲存設定
                        </button>
                        <button onClick={() => setCurrentView && setCurrentView('addRepair')} className="bg-slate-600 hover:bg-slate-500 text-white px-5 py-2.5 rounded-xl font-black flex items-center gap-2 shadow-sm">
                            <ChevronRight className="w-4 h-4 rotate-180"/>返回首頁
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200 shadow-sm">
                <div className="w-full px-4 sm:px-6 py-5 flex flex-wrap gap-3">
                    <button onClick={() => setActiveTab('categories')} className={tabClass('categories')}>修繕資料設定</button>
                    <button onClick={() => setActiveTab('fixMethods')} className={tabClass('fixMethods')}>報修方式設定</button>
                    <button onClick={() => setActiveTab('reasons')} className={tabClass('reasons')}>歸屬原因設定</button>
                    <button onClick={() => setActiveTab('persons')} className={tabClass('persons')}>填單人員設定</button>
                </div>
            </div>

            <div className="w-full px-4 sm:px-6 py-8 space-y-6">
                {activeTab === 'categories' && (
                    <div className="grid grid-cols-1 xl:grid-cols-[380px,minmax(0,1fr)] gap-4 w-full">
                        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden min-h-[720px]">
                            <div className="px-4 py-3 bg-slate-100 border-b border-gray-200 flex items-center justify-between">
                                <div>
                                    <h3 className="font-black text-slate-800">修繕類型</h3>
                                    
                                </div>
                                <button onClick={handleAddCategory} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-3 py-2 text-sm font-black flex items-center gap-1.5 shadow-sm">
                                    <PlusCircle className="w-4 h-4"/>新增
                                </button>
                            </div>
                            <div className="p-3 bg-gray-50/80 border-b border-gray-200 grid grid-cols-[92px,1fr] gap-2">
                                <input value={newCategory.Code} onChange={e => setNewCategory(prev => ({ ...prev, Code: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 bg-white" placeholder="編號" />
                                <input value={newCategory.Name} onChange={e => setNewCategory(prev => ({ ...prev, Name: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 bg-white" placeholder="類型名稱" />
                            </div>
                            <div className="grid grid-cols-[56px,1fr,48px] px-4 py-3 text-sm font-black text-slate-500 border-b border-gray-200 bg-white">
                                <div>編號</div><div>類型名稱</div><div className="text-center">刪</div>
                            </div>
                            <div className="max-h-[560px] overflow-y-auto">
                                {categories.length === 0 ? (
                                    <div className="px-4 py-10 text-center text-slate-400 font-bold">目前無設定任何項目</div>
                                ) : categories.map((c, idx) => {
                                    const code = c.Code || c.code;
                                    return (
                                        <div key={`${code}-${idx}`} className={`grid grid-cols-[56px,1fr,48px] items-center px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors ${String(selectedCategoryCode) === String(code) ? 'bg-blue-100/70 border-l-4 border-l-blue-500' : 'bg-white hover:bg-blue-50/50'}`} onClick={() => setSelectedCategoryCode(code)}>
                                            <input value={code} onChange={e => { e.stopPropagation(); handleChangeCategory(idx, 'Code', e.target.value); }} className="w-12 border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-mono text-slate-700 bg-white" />
                                            <input value={c.Name || c.name} onChange={e => { e.stopPropagation(); handleChangeCategory(idx, 'Name', e.target.value); }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 bg-white" />
                                            <button onClick={(e) => { e.stopPropagation(); handleDeleteCategory(code); }} className="ml-auto w-8 h-8 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100 flex items-center justify-center"><Trash2 className="w-4 h-4"/></button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden min-h-[720px]">
                            <div className="px-4 py-3 bg-slate-100 border-b border-gray-200 flex items-center justify-between">
                                <div>
                                    <h3 className="font-black text-slate-800">修繕細項</h3>
                                    <p className="text-xs text-slate-500 font-bold mt-1">{selectedCatData ? `${selectedCatData.Name || selectedCatData.name} / ${selectedCategoryCode}` : '請先選擇左側修繕類型'}</p>
                                </div>
                                <button onClick={handleAddItem} disabled={!selectedCategoryCode} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl px-3 py-2 text-sm font-black flex items-center gap-1.5 shadow-sm">
                                    <PlusCircle className="w-4 h-4"/>新增細項
                                </button>
                            </div>
                            <div className="p-3 bg-gray-50/80 border-b border-gray-200 grid grid-cols-[72px,140px,140px,1fr] gap-2">
                                <input value={newItem.Sno} onChange={e => setNewItem(prev => ({ ...prev, Sno: e.target.value }))} disabled={!selectedCategoryCode} className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 bg-white disabled:bg-gray-100" placeholder="序" />
                                <input disabled value={selectedCatData?.Name || ''} className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-400 bg-white disabled:bg-gray-50" placeholder="類別" />
                                <input disabled value={selectedCategoryCode || ''} className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-400 bg-white disabled:bg-gray-50" placeholder="代碼" />
                                <input value={newItem.Name} onChange={e => setNewItem(prev => ({ ...prev, Name: e.target.value }))} disabled={!selectedCategoryCode} className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 bg-white disabled:bg-gray-100" placeholder="細項名稱" />
                            </div>
                            <div className="grid grid-cols-[64px,160px,160px,1fr,56px] px-4 py-3 text-sm font-black text-slate-500 border-b border-gray-200 bg-white">
                                <div>序</div><div>類別</div><div>代碼</div><div>細項名稱</div><div className="text-center">刪除</div>
                            </div>
                            <div className="max-h-[560px] overflow-y-auto">
                                {!selectedCategoryCode ? (
                                    <div className="px-4 py-10 text-center text-slate-400 font-bold">請先於左側點選一個修繕類型</div>
                                ) : currentItems.length === 0 ? (
                                    <div className="px-4 py-10 text-center text-slate-400 font-bold">此修繕類型目前尚未建立任何問題細項</div>
                                ) : currentItems.map((item, idx) => {
                                    const sno = item.Sno || item.sno;
                                    return (
                                        <div key={`${selectedCategoryCode}-${sno}-${idx}`} className="grid grid-cols-[64px,160px,160px,1fr,56px] items-center px-4 py-3 border-b border-gray-100 bg-white hover:bg-blue-50/40">
                                            <input value={sno} onChange={e => handleChangeItem(idx, 'Sno', e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-mono text-slate-700 bg-white" />
                                            <input value={selectedCatData?.Name || ''} disabled className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-500 bg-gray-50" />
                                            <input value={selectedCategoryCode || ''} disabled className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-500 bg-gray-50" />
                                            <input value={item.Name || item.name} onChange={e => handleChangeItem(idx, 'Name', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 bg-white" />
                                            <button onClick={() => handleDeleteItem(sno)} className="ml-auto w-8 h-8 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100 flex items-center justify-center"><Trash2 className="w-4 h-4"/></button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'fixMethods' && renderSimpleMaster('報修方式設定', '', fixMethods, setFixMethods, newFixMethod, setNewFixMethod, 'fixMethods', '例如：官方LINE')}
                {activeTab === 'reasons' && renderSimpleMaster('歸屬原因設定', '', reasons, setReasons, newReason, setNewReason, 'reasons', '例如：設計')}
                {activeTab === 'persons' && (
                    <div className="grid grid-cols-1 xl:grid-cols-[340px,minmax(0,1fr)] gap-4 w-full">
                        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                            <div className="px-4 py-3 bg-slate-100 border-b border-gray-200 flex items-center justify-between">
                                <div>
                                    <h3 className="font-black text-slate-800">填單人員設定</h3>
                                    
                                </div>
                                <span className="text-xs bg-white border border-gray-200 px-2 py-1 rounded-lg font-bold text-slate-600">{persons.length} 筆</span>
                            </div>
                            <div className="p-3 space-y-2 border-b border-gray-100 bg-gray-50/70">
                                <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-500 bg-slate-50 cursor-not-allowed" placeholder="序號 Sno" value={newPerson.Sno} readOnly />
                                <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-700 bg-white" placeholder="姓名 Name" value={newPerson.Name} onChange={e => setNewPerson(prev => ({ ...prev, Name: e.target.value }))} />
                                <button onClick={handleAddPerson} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2.5 font-bold flex items-center justify-center gap-2 shadow-sm"><PlusCircle className="w-4 h-4"/>新增人員</button>
                            </div>
                            <div className="max-h-[620px] overflow-y-auto">
                                {persons.length === 0 ? (
                                    <div className="px-4 py-10 text-center text-slate-400 font-bold">目前尚無人員資料</div>
                                ) : persons.map((row, idx) => (
                                    <div key={`persons-${row.Sno || row.Code}-${idx}`} className="px-4 py-3 border-b border-gray-100 bg-white hover:bg-blue-50/40">
                                        <div className="flex items-center gap-2 mb-2">
                                            <input value={row.Sno || row.Code || ''} readOnly className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-mono text-slate-500 bg-slate-50 cursor-not-allowed" />
                                            <button onClick={() => handleDeletePerson(row.Sno || row.Code)} className="ml-auto w-8 h-8 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100 flex items-center justify-center"><Trash2 className="w-4 h-4"/></button>
                                        </div>
                                        <input value={row.Name || ''} onChange={e => handleChangePerson(idx, 'Name', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 bg-white" />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="bg-white border border-dashed border-gray-200 rounded-2xl shadow-sm flex items-center justify-center min-h-[220px] text-center p-8">
                            <div>
                                <div className="text-lg font-black text-slate-700 mb-2">填單人員設定</div>
                                <div className="text-sm text-slate-500 font-bold leading-7">左側清單可直接編輯與刪除，<br/>完成後按右上角「儲存設定」即可寫入資料庫。</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function LoginScreen({ onLogin }) {
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
      if(!account || !password) { alert("請輸入帳號密碼"); return; }
      setLoading(true);
      try {
          const res = await fetch(API.LOGIN, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
              body: JSON.stringify({ account, password })
          });
          
          const textData = await res.text();
          if (!res.ok) throw new Error(textData || `伺服器回應 ${res.status}`);
          if (!textData) throw new Error("伺服器回傳了空白內容");
          
          const rawData = JSON.parse(textData);
          
          let data = Array.isArray(rawData) ? rawData[0] : rawData;
          if (data && data.json) data = data.json;
          
          let fetchedName = data?.Name || data?.name || 
                            data?.data?.Name || data?.data?.name || 
                            data?.user?.Name || data?.user?.name || 
                            '';
          
          let finalName = fetchedName;
          if (!finalName) {
              finalName = (account.toLowerCase() === 'root') ? '系統管理者' : account;
          }

          onLogin({ account: account, name: finalName });
      } catch (err) {
          handleApiError(err, '系統登入');
      } finally {
          setLoading(false);
      }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl overflow-hidden flex flex-col border border-gray-100">
        <div className="bg-blue-600 p-8 text-white text-center flex flex-col items-center justify-center relative">
          <div className="bg-white/10 p-4 rounded-full mb-4">
            <Wrench className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-widest whitespace-nowrap mb-1">AHMShr房屋報修管理系統</h1>
          <span className="text-sm tracking-widest text-blue-100">簡單 • 標準 • 專業</span>
        </div>
        
        <div className="p-8 space-y-6">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2 flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600"/> 帳號
            </label>
            <input 
              type="text" 
              value={account}
              onChange={e => setAccount(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all font-bold" 
              placeholder="請輸入使用者帳號" 
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2 flex items-center gap-2">
              <Lock className="w-4 h-4 text-blue-600"/> 密碼
            </label>
            <input 
              type="password" 
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all font-bold" 
              placeholder="請輸入密碼" 
            />
          </div>
          <button 
            onClick={handleLogin}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:bg-blue-400"
          >
            {loading ? <Loader className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5"/>} 登入系統
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// 1. 新增報修功能 (含相片上傳機制)
// ==========================================
function AddRepairScreen({ globalData, userInfo, fixMethodOptions = [], reasonOptions = [] }) {
  const [selectedProj, setSelectedProj] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [units, setUnits] = useState([]);
  const [hideUndelivered, setHideUndelivered] = useState(true);
  const [customerInfo, setCustomerInfo] = useState({ name: '', tel: '', addr: '', date3: '' });
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isContractorModalOpen, setIsContractorModalOpen] = useState(false);
  const [modalTargetItemIndex, setModalTargetItemIndex] = useState(null); 
  
  const fileInputRef = useRef(null);
  const currentUploadItemIndex = useRef(null);
  const [viewPhoto, setViewPhoto] = useState(null); 
  const [editingImage, setEditingImage] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  
  const [form, setForm] = useState({
      sdate: new Date().toISOString().split('T')[0], 
      wflg: '0', 
      note1: '',
      items: [{ sno: 1, acode: '', asno: '', note: '', flg: '0', prdtcode: '', prdtName: '', date1: '', note2: '', photos: [] }]
  });

  const fixMethodSelectOptions = (Array.isArray(fixMethodOptions) && fixMethodOptions.length > 0
    ? fixMethodOptions
    : Object.entries(WFLG_MAP).map(([Code, Name]) => ({ Code, Name }))
  ).map(item => ({ value: String(item.Code ?? item.Sno ?? ''), label: String(item.Name ?? '') }));

  const reasonSelectOptions = (Array.isArray(reasonOptions) && reasonOptions.length > 0
    ? reasonOptions
    : Object.entries(FLG_MAP).map(([Code, Name]) => ({ Code, Name }))
  ).map(item => ({ value: String(item.Code ?? item.Sno ?? ''), label: String(item.Name ?? '') }));

  const filteredUnits = useMemo(() => {
    if (!hideUndelivered) return units;
    return units.filter((u) => {
      const date3 = u?.Date3 ?? u?.date3;
      return date3 !== null && date3 !== undefined && String(date3).trim() !== '';
    });
  }, [units, hideUndelivered]);

  useEffect(() => {
    const hasData = 
      selectedProj !== '' || 
      selectedUnit !== '' || 
      form.note1 !== '' || 
      form.items.some(item => item.acode !== '' || item.note !== '' || (item.photos && item.photos.length > 0));

    const handleBeforeUnload = (e) => {
      if (hasData) {
        e.preventDefault();
        e.returnValue = ''; 
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [selectedProj, selectedUnit, form]);

  useEffect(() => {
      if(!selectedProj) { setUnits([]); return; }
      const fetchUnits = async () => {
          setLoadingUnits(true);
          try {
              const res = await fetch(`${API.GET_REPAIR_DATA}?worksno=${selectedProj}`, {
                  method: 'GET',
                  headers: { 'ngrok-skip-browser-warning': 'true' }
              });
              if (!res.ok) throw new Error(`伺服器回應 ${res.status}`);
              const textData = await res.text();
              if (!textData || textData.trim() === '') return setUnits([]);

              const rawData = JSON.parse(textData);
              let data = Array.isArray(rawData) ? rawData[0] : rawData;
              if (data && data.json) data = data.json;
              
              const sqlJsonKey = data ? Object.keys(data).find(k => k.startsWith('JSON_')) : null;
              if (sqlJsonKey) {
                  try {
                      const parsedRoot = JSON.parse(data[sqlJsonKey]);
                      data = Array.isArray(parsedRoot) ? parsedRoot : [parsedRoot];
                  } catch (e) {}
              } else {
                  data = parseN8nResponse(rawData);
              }
              setUnits(Array.isArray(data) ? data : []);
          } catch(err) { handleApiError(err, '查詢戶別資料'); setUnits([]); } 
          finally { setLoadingUnits(false); }
      };
      fetchUnits();
  }, [selectedProj]);

  useEffect(() => {
    if (!hideUndelivered || !selectedUnit) return;

    const selected = units.find(u => String(u.Hcode || u.hcode) === String(selectedUnit));
    const date3 = selected?.Date3 ?? selected?.date3;
    const delivered = date3 !== null && date3 !== undefined && String(date3).trim() !== '';

    if (!delivered) {
      setSelectedUnit('');
      setCustomerInfo({ name: '', tel: '', addr: '', date3: '' });
    }
  }, [hideUndelivered, selectedUnit, units]);

  const handleUnitChange = (e) => {
    const unitCode = e.target.value;
    setSelectedUnit(unitCode);
    const unitData = units.find(u => String(u.Hcode || u.hcode) === String(unitCode));
    if (unitData) {
      setCustomerInfo({ 
          name: unitData.CustomerName || unitData.CustName || unitData.Name || '', 
          tel: unitData.Tel1 || unitData.tel1 || '', 
          addr: unitData.Addr1 || unitData.Addr || unitData.addr || '',
          date3: unitData.Date3 ? unitData.Date3.split('T')[0] : (unitData.date3 ? unitData.date3.split('T')[0] : '')
      });
    } else {
      setCustomerInfo({ name: '', tel: '', addr: '', date3: '' });
    }
  };

  const handleItemChange = (index, field, value) => {
      const newItems = [...form.items];
      newItems[index][field] = value;
      if (field === 'acode') newItems[index].asno = '';
      setForm({ ...form, items: newItems });
  };

  const handleAddItem = () => {
      setForm({
          ...form,
          items: [
              ...form.items, 
              { sno: form.items.length + 1, acode: '', asno: '', note: '', flg: '0', prdtcode: '', prdtName: '', date1: '', note2: '', photos: [] }
          ]
      });
  };

  const handleRemoveItem = (indexToRemove) => {
      const newItems = form.items.filter((_, idx) => idx !== indexToRemove);
      const reindexedItems = newItems.map((item, idx) => ({ ...item, sno: idx + 1 }));
      setForm({ ...form, items: reindexedItems });
  };

  const triggerFileSelect = (itemIndex) => { 
      currentUploadItemIndex.current = itemIndex; 
      if (fileInputRef.current) fileInputRef.current.click(); 
  };

  const handlePhotoUpload = async (event) => {
      const file = event.target.files[0];
      const targetIndex = currentUploadItemIndex.current;
      if (!file || targetIndex === null) return;
      
      const localBlobUrl = URL.createObjectURL(file);
      const newItems = [...form.items];
      const currentPhotos = newItems[targetIndex].photos || [];
      
      if (currentPhotos.length >= 3) { 
          alert("最多只能上傳 3 張照片"); 
      } else {
          newItems[targetIndex].photos = [...currentPhotos, localBlobUrl];
          setForm({ ...form, items: newItems });
      }
      event.target.value = '';
  };

  const handleDeletePhoto = (itemIndex, photoIndex) => { 
      if(window.confirm('確定刪除此照片？')) {
          const newItems = [...form.items];
          newItems[itemIndex].photos.splice(photoIndex, 1);
          setForm({ ...form, items: newItems });
      }
  };

  const handleEditImage = (itemIndex, photoSrc, photoIndex) => { 
      setEditingImage({ itemIndex, src: photoSrc, photoIndex }); 
      setEditorOpen(true); 
  };

  const handleSaveEditedImage = (newSrc) => {
      if (editingImage) {
          const newItems = [...form.items];
          newItems[editingImage.itemIndex].photos[editingImage.photoIndex] = newSrc;
          setForm({ ...form, items: newItems });
      }
      setEditorOpen(false); 
      setEditingImage(null);
  };

  const handleSave = async () => {
      const errors = [];
      if (!selectedProj) errors.push("建案");
      if (!selectedUnit) errors.push("戶別");
      if (!form.sdate) errors.push("報修日期");
      if (!form.wflg) errors.push("報修方式");

      form.items.forEach((item, index) => {
          const itemNum = `細項 ${item.sno}`;
          if (!item.acode) errors.push(`${itemNum}: 修繕項目`);
          if (!item.asno) errors.push(`${itemNum}: 發生原因`);
          if (!item.note) errors.push(`${itemNum}: 項目位置`);
          if (!item.flg) errors.push(`${itemNum}: 歸屬原因`);
          if (!item.prdtcode) errors.push(`${itemNum}: 施作廠商`);
          if (!item.date1) errors.push(`${itemNum}: 預計完成日`);
      });

      if (errors.length > 0) {
          alert(`請填寫以下必填欄位：\n\n${errors.map(e => `• ${e}`).join('\n')}`);
          return;
      }

      setSaving(true);
      try {
          const projData = globalData.projects.find(p => String(p.Sno || p.sno || p.Wcode || p.wcode) === String(selectedProj));
          const actualWcode = projData ? (projData.Wcode || projData.wcode) : selectedProj; 
          const actualSno = projData ? (projData.Sno || projData.sno) : selectedProj; 
          const projNameForUpload = projData ? (projData.Wname || projData.wname || projData.Name) : '未知建案';
          
          const d = new Date(form.sdate);
          const rocYearStr = String(d.getFullYear() - 1911).slice(-2);
          const monthStr = String(d.getMonth() + 1).padStart(2, '0');
          const dayStr = String(d.getDate()).padStart(2, '0');
          const snoStr = String(actualSno).padStart(3, '0');
          const generatedAserno = `${snoStr}${rocYearStr}${monthStr}${dayStr}001`;

          let uploadErrors = 0;
          let lastErrorMsg = "";

          const processedItems = await Promise.all(form.items.map(async (item) => {
              const newPhotos = [];
              for (const photo of (item.photos || [])) {
                  if (photo && (photo.startsWith('blob:') || photo.startsWith('data:'))) {
                      try {
                          const blobRes = await fetch(photo);
                          const blob = await blobRes.blob();
                          const file = new File([blob], `photo_${item.sno}_${Math.random().toString(36).substr(2, 9)}.jpg`, { type: "image/jpeg" });
                          
                          const formData = new FormData();
                          formData.append('file', file); 
                          formData.append('project', projNameForUpload); 
                          formData.append('unit', selectedUnit); 
                          formData.append('pathPrefix', 'public/data/uploads');
                          
                          const uploadRes = await fetch(API_IMAGE_UPLOAD, { 
                              method: 'POST', 
                              body: formData, 
                              headers: { 'ngrok-skip-browser-warning': 'true' } 
                          });
                          
                          if (uploadRes.ok) {
                              const result = await uploadRes.json();
                              if (result.filename || result.url) {
                                  newPhotos.push(`/public/data/uploads/${projNameForUpload}/${(result.filename || result.url).split('/').pop()}`);
                              } else { 
                                  newPhotos.push(photo); 
                                  uploadErrors++; 
                                  lastErrorMsg = "伺服器回傳格式不符";
                              }
                          } else { 
                              newPhotos.push(photo); 
                              uploadErrors++; 
                              lastErrorMsg = `伺服器回應錯誤碼: ${uploadRes.status}`;
                          }
                      } catch (err) { 
                          newPhotos.push(photo); 
                          uploadErrors++; 
                          lastErrorMsg = `連線失敗: ${err.message}`;
                      }
                  } else {
                      newPhotos.push(photo);
                  }
              }
              
              return {
                  sno: item.sno,
                  acode: item.acode,
                  asno: item.asno, 
                  note: item.note,
                  flg: parseInt(item.flg, 10),
                  prdtcode: item.prdtcode,
                  date1: item.date1,
                  note2: item.note2,
                  photos: newPhotos.length > 0 ? JSON.stringify(newPhotos) : '',
                  Photo: newPhotos.length > 0 ? JSON.stringify(newPhotos) : '' 
              };
          }));

          if (uploadErrors > 0) {
              if (!window.confirm(`警告：有 ${uploadErrors} 張照片上傳失敗！\n\n可能原因：${lastErrorMsg}\n\n是否仍要強制存檔？(失敗的照片將無法顯示)`)) { 
                  setSaving(false); 
                  return; 
              }
          }

          const payload = {
              wcode: actualWcode, 
              hcode: selectedUnit,
              aserno: generatedAserno, 
              sdate: form.sdate,
              name: customerInfo.name,
              tel: customerInfo.tel,
              addr: customerInfo.addr,
              wflg: parseInt(form.wflg, 10),
              note1: form.note1,
              items: processedItems
          };

          const res = await fetch(API.SUBMIT_REPAIR, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
              body: JSON.stringify(payload)
          });
          
          const textData = await res.text();
          if(!res.ok) throw new Error(textData || `伺服器錯誤 ${res.status}`);

          let finalAserno = generatedAserno;
          try {
              const resData = JSON.parse(textData);
              if(resData.aserno) finalAserno = resData.aserno;
          } catch(e) {}
          
          alert(`✅ 報修單建立成功！單號：${finalAserno}`);
          
          setForm({
              sdate: new Date().toISOString().split('T')[0], wflg: '0', note1: '',
              items: [{ sno: 1, acode: '', asno: '', note: '', flg: '0', prdtcode: '', prdtName: '', date1: '', note2: '', photos: [] }]
          });
          setSelectedUnit('');
          setCustomerInfo({name: '', tel: '', addr: '', date3: ''});
      } catch (err) {
          handleApiError(err, '儲存報修單');
      } finally {
          setSaving(false);
      }
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300 print:hidden max-w-3xl mx-auto">
      
      <input type="file" ref={fileInputRef} className="hidden" accept="image/*" capture="environment" onChange={handlePhotoUpload}/>

      <h2 className="font-bold text-blue-800 border-b-2 border-blue-200 pb-2 flex items-center gap-2">
          Step 1: 建立報修作業
          <span className="text-xs text-gray-500 font-normal ml-auto">( <span className="text-red-500">*</span> 為必填欄位 )</span>
      </h2>
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1 font-bold">建案 <span className="text-red-500">*</span></label>
            <select className="w-full border border-gray-300 rounded-md p-2 bg-gray-50 focus:ring-2 focus:ring-blue-500 font-bold text-blue-800" value={selectedProj} onChange={(e) => { setSelectedProj(e.target.value); setSelectedUnit(''); setCustomerInfo({name:'', tel:'', addr:'', date3:''}); }}>
              <option value="">請選擇建案...</option>
              {globalData.projects.map(p => {
                  const val = p.Sno || p.sno || p.Wcode || p.wcode;
                  const wcode = p.Wcode || p.wcode || '';
                  const name = p.Wname || p.wname || p.Name || '';
                  return <option key={val} value={val}>{name} ({wcode})</option>
              })}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1 font-bold">戶別 <span className="text-red-500">*</span></label>
            <div className="relative">
                <div className="absolute right-0 -top-7 flex items-center gap-1 text-xs font-bold text-gray-700">
                    <input
                        id="hide-undelivered"
                        type="checkbox"
                        checked={hideUndelivered}
                        onChange={(e) => setHideUndelivered(e.target.checked)}
                        className="w-4 h-4 accent-blue-600"
                    />
                    <label htmlFor="hide-undelivered" className="cursor-pointer select-none">未交屋者略</label>
                </div>
                <select className="w-full border border-gray-300 rounded-md p-2 bg-gray-50 focus:ring-2 focus:ring-blue-500 font-bold text-blue-800" value={selectedUnit} onChange={handleUnitChange} disabled={!selectedProj || loadingUnits}>
                <option value="">{loadingUnits ? '載入資料庫中...' : '請選擇戶別...'}</option>
                {filteredUnits.map((u, i) => {
                    const code = u.Hcode || u.hcode;
                    const name = u.CustomerName || u.CustName || u.Name || '';
                    return <option key={i} value={code}>{code} {name ? `(${name})` : ''}</option>
                })}
                </select>
                {loadingUnits && <Loader className="w-4 h-4 text-blue-500 animate-spin absolute right-3 top-2.5" />}
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-sm grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
              <div><span className="text-gray-500">姓名: </span><span className="font-semibold text-gray-800">{customerInfo.name || '---'}</span></div>
              <div><span className="text-gray-500">地址: </span><span className="font-semibold text-gray-800">{customerInfo.addr || '---'}</span></div>
          </div>
          <div className="space-y-2 md:pl-4 md:border-l border-blue-200">
              <div><span className="text-gray-500">電話: </span><span className="font-semibold text-gray-800">{customerInfo.tel || '---'}</span></div>
              <div className="flex items-center"><span className="text-gray-500 mr-1">交屋日: </span><span className="font-semibold text-gray-800">{customerInfo.date3 || '---'}</span></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1 font-bold">報修日期 <span className="text-red-500">*</span></label>
            <input type="date" className="w-full border border-gray-300 rounded-md p-2" value={form.sdate} onChange={e => setForm({...form, sdate: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1 font-bold">報修方式 <span className="text-red-500">*</span></label>
            <select className="w-full border border-gray-300 rounded-md p-2" value={form.wflg} onChange={e => setForm({...form, wflg: e.target.value})}>
              {fixMethodSelectOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
            <label className="block text-sm text-gray-600 mb-1 font-bold">申請概要</label>
            <textarea className="w-full border border-gray-300 rounded-md p-3 h-24 placeholder-gray-400" placeholder="請簡述狀況 (選填)..." value={form.note1} onChange={e => setForm({...form, note1: e.target.value})}></textarea>
        </div>
      </div>

      <h2 className="font-bold text-blue-800 border-b-2 border-blue-200 pb-2 mt-6">Step 2: 報修作業細項</h2>
      
      {form.items.map((item, index) => {
          const currentPhotos = item.photos || [];
          const canAddPhoto = currentPhotos.length < 3;

          return (
          <div key={index} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 space-y-4 relative mb-4">
            
            <div className="absolute top-2 right-4 flex items-center gap-2">
                {form.items.length > 1 && (
                    <button onClick={() => handleRemoveItem(index)} className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors" title="刪除此細項">
                        <Trash2 className="w-4 h-4"/>
                    </button>
                )}
                <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded shadow-sm border border-gray-200">序號: {item.sno}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <label className="block text-xs text-gray-700 font-bold mb-1">修繕項目 <span className="text-red-500">*</span></label>
                <select className="w-full border border-gray-300 rounded-md p-2 text-sm font-bold" value={item.acode} onChange={e => handleItemChange(index, 'acode', e.target.value)}>
                   <option value="">請選擇</option>
                   {globalData.categories.map(c => <option key={c.Code || c.code} value={c.Code || c.code}>{c.Code || c.code} {c.Name || c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-700 font-bold mb-1">發生原因 <span className="text-red-500">*</span></label>
                <select className="w-full border border-gray-300 rounded-md p-2 text-sm font-bold" value={item.asno} onChange={e => handleItemChange(index, 'asno', e.target.value)}>
                   <option value="">請選擇</option>
                   {globalData.items.filter(i => String(i.Pcode || i.pcode) === String(item.acode)).map(i => <option key={i.Sno || i.sno} value={i.Sno || i.sno}>{i.Sno || i.sno} {i.Name || i.name}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-700 font-bold mb-1">項目位置 <span className="text-red-500">*</span></label>
                <input type="text" className="w-full border border-gray-300 rounded-md p-2 text-sm font-bold" value={item.note} onChange={e => handleItemChange(index, 'note', e.target.value)} placeholder="例如：廚房" />
              </div>
              <div>
                <label className="block text-xs text-gray-700 font-bold mb-1">歸屬原因 <span className="text-red-500">*</span></label>
                <select className="w-full border border-gray-300 rounded-md p-2 text-sm font-bold" value={item.flg} onChange={e => handleItemChange(index, 'flg', e.target.value)}>
                    {reasonSelectOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-700 font-bold mb-1">施作廠商 <span className="text-red-500">*</span></label>
                <div className="flex gap-1">
                    <input 
                        type="text" 
                        readOnly 
                        className="w-full border border-gray-300 rounded-md p-2 text-sm bg-gray-50 text-blue-700 font-bold cursor-pointer hover:bg-gray-100 transition-colors" 
                        placeholder="請點選右側按鈕" 
                        value={item.prdtName ? `${item.prdtName}` : ''} 
                        onClick={() => { setModalTargetItemIndex(index); setIsContractorModalOpen(true); }}
                    />
                    <button onClick={() => { setModalTargetItemIndex(index); setIsContractorModalOpen(true); }} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 rounded-md font-bold border border-gray-300 transition-colors">...</button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-700 font-bold mb-1">預計完成日 <span className="text-red-500">*</span></label>
                <input type="date" className="w-full border border-gray-300 rounded-md p-2 text-sm" value={item.date1} onChange={e => handleItemChange(index, 'date1', e.target.value)} />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-700 font-bold mb-1">備註說明</label>
              <textarea className="w-full border border-gray-300 rounded-md p-2 h-16 text-sm" placeholder="選填" value={item.note2} onChange={e => handleItemChange(index, 'note2', e.target.value)}></textarea>
            </div>

            <div className="grid grid-cols-4 gap-2 pt-2 border-t border-gray-100 mt-2">
                {currentPhotos.map((src, photoIdx) => (
                    <div key={photoIdx} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 shadow-sm">
                        <SecureImage 
                            src={resolvePhotoUrl(src)} 
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                            onClick={() => setViewPhoto({ url: resolvePhotoUrl(src), name: `明細項次 #${item.sno}`, note: item.note })} 
                        />
                        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <button 
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditImage(index, src, photoIdx); }} 
                                className="p-1 bg-blue-600/80 text-white rounded hover:bg-blue-700 shadow" 
                                title="編輯/標示"
                            >
                                <Palette size={12}/>
                            </button>
                            <button 
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeletePhoto(index, photoIdx); }} 
                                className="p-1 bg-red-600/80 text-white rounded hover:bg-red-700 shadow" 
                                title="刪除"
                            >
                                <Trash2 size={12}/>
                            </button>
                        </div>
                        <div className={`absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[9px] text-white truncate text-center font-bold tracking-wider ${src.startsWith('blob:') ? 'bg-orange-500/90' : 'bg-green-600/90'}`}>
                            {src.startsWith('blob:') ? '尚未存檔' : '已上傳'}
                        </div>
                    </div>
                ))}
                {canAddPhoto && (
                    <button 
                        onClick={(e) => { e.preventDefault(); triggerFileSelect(index); }} 
                        className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 transition-all text-gray-500 hover:bg-gray-100 hover:border-gray-400"
                    >
                        <Camera className="w-5 h-5"/>
                        <span className="text-[10px] font-bold text-gray-400">拍照/選圖</span>
                        <span className="text-[9px] text-gray-400">({currentPhotos.length}/3)</span>
                    </button>
                )}
            </div>

          </div>
      )})}
      
      <div className="flex flex-col md:flex-row justify-between pt-2 gap-4">
           <button onClick={handleAddItem} className="text-blue-600 border-2 border-blue-600 bg-blue-50 px-6 py-2.5 rounded-lg font-bold hover:bg-blue-100 active:scale-95 transition-all flex justify-center items-center gap-2">
             <Plus className="w-5 h-5"/> 增加報修細項
           </button>
           <button onClick={handleSave} disabled={saving} className="bg-blue-600 text-white px-8 py-2.5 rounded-lg font-bold shadow-md hover:bg-blue-700 active:scale-95 transition-all flex justify-center items-center gap-2 disabled:bg-blue-400">
             {saving ? <Loader className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5"/>} 
             {saving ? '圖片與資料儲存中...' : '確認存檔'}
           </button>
      </div>
      
      <ContractorSelectModal 
          isOpen={isContractorModalOpen} 
          onClose={() => setIsContractorModalOpen(false)}
          contractors={globalData.contractors}
          onSelect={(c) => {
              if(modalTargetItemIndex !== null) {
                  const newItems = [...form.items];
                  newItems[modalTargetItemIndex].prdtcode = c.Prdtcode||c.prdtcode||c.Code||c.code;
                  newItems[modalTargetItemIndex].prdtName = c.Name||c.name;
                  setForm({...form, items: newItems});
              }
              setIsContractorModalOpen(false);
          }}
      />

      {viewPhoto && (
          <div className="fixed inset-0 z-[10000] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setViewPhoto(null)}>
              <div className="absolute top-4 right-4 flex gap-4 z-20">
                  {(!viewPhoto.url.startsWith('blob:')) && (
                      <button className="p-2 bg-blue-600/80 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-2 font-bold text-sm shadow-lg border border-blue-400" onClick={(e) => { e.stopPropagation(); window.open(viewPhoto.url, '_blank'); }}>
                          <ExternalLink className="w-4 h-4"/> <span className="hidden md:inline">無法顯示？開啟原圖</span>
                      </button>
                  )}
                  <button className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); setViewPhoto(null); }}>
                      <X className="w-8 h-8"/>
                  </button>
              </div>
              <div className="w-full max-w-5xl flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                  <SecureImage src={viewPhoto.url} alt="Preview" className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl bg-black" />
                  <div className="bg-gray-900/80 p-4 rounded-xl border border-gray-700 backdrop-blur text-center min-w-[300px] max-w-2xl mt-4">
                      <h3 className="text-xl font-bold text-white mb-1">{viewPhoto.name}</h3>
                      <p className="text-gray-300 text-sm">{viewPhoto.note || "無備註描述"}</p>
                  </div>
              </div>
          </div>
      )}

      <ImageEditorModal 
          isOpen={editorOpen} 
          onClose={() => { setEditorOpen(false); setEditingImage(null); }} 
          imageSrc={editingImage?.src} 
          onSave={handleSaveEditedImage} 
      />

    </div>
  );
}

// ==========================================
// 2. 廠商派工修繕作業
// ==========================================
function DispatchScreen({ globalData, userInfo }) {
  const [selectedContractor, setSelectedContractor] = useState(''); 
  const [selectedContractorName, setSelectedContractorName] = useState('');
  const [isContractorModalOpen, setIsContractorModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false); 
  
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [dispatchPerson, setDispatchPerson] = useState('');
  const [dispatchNote, setDispatchNote] = useState('');
  
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [pendingTickets, setPendingTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  
  useEffect(() => {
    const hasData = selectedContractor !== '' || dispatchNote !== '' || selectedItems.size > 0;

    const handleBeforeUnload = (e) => {
      if (hasData) {
        e.preventDefault();
        e.returnValue = ''; 
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [selectedContractor, dispatchNote, selectedItems]);

  const handleSearchPending = async () => {
      if(!selectedContractor) { alert("請先選擇廠商"); return; }
      setLoading(true);
      try {
          const res = await fetch(API.GET_PENDING_DISPATCH, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
              body: JSON.stringify({ prdtcode: selectedContractor })
          });
          
          const textData = await res.text();
          if(!res.ok) throw new Error(textData || `伺服器回應 ${res.status}`);
          
          if(!textData || textData.trim() === '') {
              setPendingTickets([]);
              setSelectedItems(new Set());
              return;
          }
          
          const rawData = JSON.parse(textData);

          const parsedData = parseN8nResponse(rawData).map((item, index) => {
              const baseNo = item.no || item.Aserno || item.aserno;
              const sno = item.Sno || item.sno || index; 
              return {
                  ...item,
                  _uid: `${baseNo}_${sno}`
              };
          });
          
          const filtered = selectedProjects.length > 0 
              ? parsedData.filter(d => selectedProjects.includes(String(d.Wcode || d.wcode || d.proj))) 
              : parsedData;
              
          setPendingTickets(filtered);
          setSelectedItems(new Set(filtered.map(f => f._uid)));
      } catch (err) {
          handleApiError(err, '查詢待派工清單');
          setPendingTickets([]); 
      } finally {
          setLoading(false);
      }
  };

  const handleGenerateDispatch = async () => {
      const errors = [];
      if (!selectedContractor) errors.push("派工廠商");
      if (!dispatchDate) errors.push("派修日期");
      if (!dispatchPerson) errors.push("填單人員");

      if (errors.length > 0) {
          alert(`請填寫以下必填欄位：\n\n${errors.map(e => `• ${e}`).join('\n')}`);
          return;
      }
      if (selectedItems.size === 0) { 
          alert('請勾選至少一筆報修明細'); 
          return; 
      }
      
      setDispatching(true);
      try {
          const d = new Date(dispatchDate);
          const rocYearStr = String(d.getFullYear() - 1911).slice(-2);
          const monthStr = String(d.getMonth() + 1).padStart(2, '0');
          const dayStr = String(d.getDate()).padStart(2, '0');
          const datePrefix = `HS${rocYearStr}${monthStr}${dayStr}`;

          const itemsToDispatch = pendingTickets.filter(t => selectedItems.has(t._uid)).map(t => ({
               Workcode: t.Wcode || t.wcode || t.proj,
               Mcode: t.no || t.Aserno || t.aserno,
               Prdtcode: selectedContractor,
               Sdate: dispatchDate,
               Hcode: t.unit || t.Hcode || t.hcode,
               Note: dispatchNote || `派修項目: ${t.AcodeName || ''} ${t.AsnoName || t.item || ''}`,
               Bank: dispatchPerson 
          }));

          const payload = { 
              action: 'batch_dispatch', 
              datePrefix: datePrefix,
              items: itemsToDispatch 
          };

          const res = await fetch(API.SUBMIT_DISPATCH, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
              body: JSON.stringify(payload)
          });
          
          const textData = await res.text();
          if(!res.ok) throw new Error(textData || `伺服器回應 ${res.status}`);
          
          alert('✅ 派修單產生成功！');
          setPendingTickets(prev => prev.filter(t => !selectedItems.has(t._uid)));
          setSelectedItems(new Set());
          setDispatchNote('');
      } catch (err) {
          handleApiError(err, '產生派修單');
      } finally {
          setDispatching(false);
      }
  };

  const toggleCheck = (id) => {
      const newSet = new Set(selectedItems);
      if(newSet.has(id)) newSet.delete(id); else newSet.add(id);
      setSelectedItems(newSet);
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300 print:hidden max-w-3xl mx-auto">
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">選擇派工廠商 <span className="text-red-500">*</span></label>
                <div className="flex gap-1">
                    <input 
                        type="text" 
                        readOnly 
                        className="w-full border border-gray-300 rounded-md p-2 text-sm bg-gray-50 text-blue-700 font-bold cursor-pointer hover:bg-gray-100 transition-colors" 
                        placeholder="點選右側按鈕選擇" 
                        value={selectedContractorName ? `${selectedContractorName}` : ''} 
                        onClick={() => setIsContractorModalOpen(true)}
                    />
                    <button onClick={() => setIsContractorModalOpen(true)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 rounded-md font-bold border border-gray-300 transition-colors">...</button>
                </div>
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">派修日期 <span className="text-red-500">*</span></label>
                <input type="date" className="w-full border border-gray-300 rounded-md p-2 bg-white font-bold text-gray-800" value={dispatchDate} onChange={e => setDispatchDate(e.target.value)} />
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">選擇建案 (供畫面過濾用)</label>
                <div className="flex gap-1">
                    <input 
                        type="text" 
                        readOnly 
                        className="w-full border border-gray-300 rounded-md p-2 text-sm bg-gray-50 text-blue-700 font-bold cursor-pointer hover:bg-gray-100 transition-colors" 
                        placeholder={selectedProjects.length > 0 ? `已選擇 ${selectedProjects.length} 個建案` : "請點選右側按鈕選擇"} 
                        value={selectedProjects.length > 0 ? `已選擇 ${selectedProjects.length} 個建案` : ''} 
                        onClick={() => setIsProjectModalOpen(true)}
                    />
                    <button onClick={() => setIsProjectModalOpen(true)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 rounded-md font-bold border border-gray-300 transition-colors">...</button>
                </div>
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">填單人員 <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                    <select 
                        className="flex-1 border border-gray-300 rounded-md p-2 bg-white font-bold text-blue-700 focus:ring-2 focus:ring-blue-500" 
                        value={dispatchPerson} 
                        onChange={e => setDispatchPerson(e.target.value)}
                    >
                        <option value="">選擇人員...</option>
                        {globalData.persons?.map((p, idx) => (
                            <option key={idx} value={p.Name || p.name}>{p.Name || p.name}</option>
                        ))}
                    </select>
                    <button onClick={handleSearchPending} disabled={loading} className="bg-gray-800 hover:bg-gray-900 text-white px-5 py-2 rounded-md font-bold flex items-center gap-2 transition-colors">
                        {loading ? <Loader className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4"/>} 搜尋
                    </button>
                </div>
            </div>
        </div>
        
        <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">說明派修內容</label>
            <textarea 
                className="w-full border border-gray-300 rounded-md p-3 h-20 bg-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 font-bold text-gray-700" 
                placeholder="輸入派修內容 (選填)..." 
                value={dispatchNote} 
                onChange={e => setDispatchNote(e.target.value)}
            ></textarea>
        </div>
      </div>

      <div className="bg-white p-0 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-3 bg-orange-50 border-b border-orange-200 flex items-center justify-between text-orange-800 font-bold">
          <div className="flex items-center"><CheckSquare size={18} className="mr-2" /> 勾選派修單項目 <span className="text-red-500 ml-1 text-lg">*</span></div>
          <span className="text-xs bg-orange-200 text-orange-900 px-2 py-0.5 rounded-full">共 {pendingTickets.length} 筆</span>
        </div>
        
        <div className="overflow-x-auto max-h-[400px]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-700 bg-gray-100 sticky top-0 shadow-sm z-10">
              <tr>
                <th className="px-3 py-3 text-center w-12">
                   <input type="checkbox" className="w-4 h-4 rounded text-blue-600" 
                          checked={selectedItems.size > 0 && selectedItems.size === pendingTickets.length}
                          onChange={(e) => {
                              if(e.target.checked) setSelectedItems(new Set(pendingTickets.map(t => t._uid)));
                              else setSelectedItems(new Set());
                          }}/>
                </th>
                <th className="px-3 py-3">申請單號</th>
                <th className="px-3 py-3">案別/戶別</th>
                <th className="px-3 py-3">修繕項目</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pendingTickets.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-10 text-gray-400 font-bold bg-gray-50/50">目前查無待派工項目，請確認該廠商是否有尚未派工的報修單</td></tr>
              ) : (
                  pendingTickets.map((ticket) => {
                    const tId = ticket._uid;
                    const isChecked = selectedItems.has(tId);
                    return (
                        <tr key={tId} className={`bg-white hover:bg-orange-50/50 transition-colors cursor-pointer ${isChecked ? 'bg-orange-50' : ''}`} onClick={() => toggleCheck(tId)}>
                        <td className="px-3 py-3 text-center">
                            <input type="checkbox" className="w-4 h-4 text-orange-600 rounded" checked={isChecked} readOnly />
                        </td>
                        <td className="px-3 py-3 font-mono text-xs text-gray-500">
                            {ticket.no || ticket.Aserno || ticket.aserno}
                            {ticket.Sno || ticket.sno ? ` - ${ticket.Sno || ticket.sno}` : ''}
                        </td>
                        <td className="px-3 py-3">
                            <div className="font-bold text-gray-800">{ticket.proj || ticket.Wname || ticket.Wcode}</div>
                            <div className="text-gray-500 text-xs mt-0.5">{ticket.unit || ticket.Hcode}</div>
                        </td>
                        <td className="px-3 py-3">
                            <div className="font-bold text-blue-700">
                                {ticket.AcodeName ? `${ticket.AcodeName} - ` : ''}{ticket.AsnoName || ticket.item || '未命名缺失'}
                            </div>
                            <div className="text-gray-500 text-xs mt-0.5">位置: {ticket.Note || ticket.loc || '無'}</div>
                        </td>
                        </tr>
                    )
                  })
              )}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 bg-gray-50 flex justify-end border-t border-gray-200">
           <button onClick={handleGenerateDispatch} disabled={dispatching || pendingTickets.length === 0} className="bg-orange-500 hover:bg-orange-600 active:scale-95 disabled:bg-orange-300 text-white px-6 py-2.5 rounded-lg font-bold shadow-md flex items-center gap-2 transition-all">
             {dispatching ? <Loader className="w-5 h-5 animate-spin"/> : <UserCog className="w-5 h-5"/>}
             {dispatching ? '產生中...' : '產生派修單'}
           </button>
        </div>
      </div>
      
      <ContractorSelectModal 
          isOpen={isContractorModalOpen} 
          onClose={() => setIsContractorModalOpen(false)}
          contractors={globalData.contractors}
          onSelect={(c) => {
              setSelectedContractor(c.Prdtcode||c.prdtcode||c.Code||c.code);
              setSelectedContractorName(c.Name||c.name);
              setIsContractorModalOpen(false);
          }}
      />

      <ProjectSelectModal 
          isOpen={isProjectModalOpen} 
          onClose={() => setIsProjectModalOpen(false)}
          projects={globalData.projects}
          selectedProjects={selectedProjects}
          setSelectedProjects={setSelectedProjects}
      />
    </div>
  );
}

// ==========================================
// 3. 追蹤查詢 (加入查看明細、列印與一覽表)
// ==========================================
function TrackingScreen({ globalData, userInfo }) {
  const [tab, setTab] = useState('client'); 
  const [searchProj, setSearchProj] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  
  const [startDate, setStartDate] = useState(() => {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const [hideCompletedClient, setHideCompletedClient] = useState(true);
  const [hideCompletedVendor, setHideCompletedVendor] = useState(true);

  const [ovProjects, setOvProjects] = useState([]);
  const [isOvProjectModalOpen, setIsOvProjectModalOpen] = useState(false);
  const [ovStatus, setOvStatus] = useState('all'); 
  const [ovSort, setOvSort] = useState('date'); 
  const [ovResults, setOvResults] = useState([]);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  
  const [selectedTicket, setSelectedTicket] = useState(null); 
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailType, setDetailType] = useState(null); 
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState(new Set()); 
  const [savingStatus, setSavingStatus] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null); 

  const signatureRef = useRef(null);

  useEffect(() => { 
      if (tab !== 'overview') setResults([]); 
  }, [tab]);

const handleSearch = async () => {
  setLoading(true);
  try {
    let url = '';
    let payload = {};

    if (tab === 'client') {
      url = API.SEARCH_CLIENT;
      payload = {
        wcode: searchProj,
        startDate,
        endDate,
        account: userInfo?.account
      };
    } else if (tab === 'overview') {
      url = API.SEARCH_CLIENT_OVERVIEW;
      payload = {
        wcode: '',
        startDate,
        endDate,
        account: userInfo?.account
      };
    } else {
      url = API.SEARCH_VENDOR;
      payload = {
        keyword: searchKeyword,
        startDate,
        endDate,
        account: userInfo?.account
      };
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
      },
      body: JSON.stringify(payload)
    });

    const textData = await res.text();
    if (!res.ok) throw new Error(textData || `伺服器回應 ${res.status}`);
    if (!textData) throw new Error("伺服器回傳空白內容，請檢查 API");

    const rawData = JSON.parse(textData);
    const parsedResponse = parseN8nResponse(rawData);

    if (tab === 'overview') {
      setOvResults(parsedResponse);
    } else {
      setResults(parsedResponse);
    }
  } catch (err) {
    handleApiError(err, '追蹤查詢');
    if (tab === 'overview') setOvResults([]);
    else setResults([]);
  } finally {
    setLoading(false);
  }
};

  const handleViewDetail = async (ticket, type) => {
      setDetailType(type);
      setSelectedTicket(ticket); 
      setDetailModalOpen(true);
      setDetailLoading(true);
      setDetailData([]);
      setCheckedItems(new Set());

      try {
          const url = type === 'client' ? API.GET_REPAIR_DETAIL : API.GET_DISPATCH_DETAIL;
          const payload = type === 'client' ? { aserno: ticket.Aserno } : { mno: ticket.displayMno };

          const res = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
              body: JSON.stringify(payload)
          });
          
          const textData = await res.text();
          if(!res.ok) throw new Error(textData || `伺服器回應 ${res.status}`);
          if(!textData) throw new Error("伺服器回傳空白內容");
          
          const rawData = parseN8nResponse(JSON.parse(textData));
          setDetailData(rawData);

          const initialChecked = new Set();
          rawData.forEach(row => {
              if (row.IsCompleted === 1 || row.IsCompleted === true || row.Date2) {
                  initialChecked.add(`${row.Aserno || row.Mcode}_${row.Sno}`);
              }
          });
          setCheckedItems(initialChecked);

      } catch (err) {
          handleApiError(err, '載入明細');
          setDetailModalOpen(false);
      } finally {
          setDetailLoading(false);
      }
  };

  const toggleItemCheck = (uid) => {
      const newSet = new Set(checkedItems);
      if (newSet.has(uid)) newSet.delete(uid);
      else newSet.add(uid);
      setCheckedItems(newSet);
  };

  const toggleAllChecks = (e) => {
      if (e.target.checked) {
          setCheckedItems(new Set(detailData.map(row => `${row.Aserno || row.Mcode}_${row.Sno}`)));
      } else {
          setCheckedItems(new Set());
      }
  };

  const handleSaveStatus = async () => {
      setSavingStatus(true);
      try {
          let signatureUrl = null;
          
          const signatureBase64 = signatureRef.current?.getSignatureImage();
          if (signatureBase64 && detailData.length > 0) {
              try {
                  const blobRes = await fetch(signatureBase64);
                  const blob = await blobRes.blob();
                  const file = new File([blob], `sign_${detailData[0].Aserno || detailData[0].Mcode}.png`, { type: "image/png" });
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('project', detailData[0].Wname || 'Signature');
                  formData.append('pathPrefix', 'public/data/uploads');

                  const uploadRes = await fetch(API_IMAGE_UPLOAD, { 
                      method: 'POST', 
                      body: formData, 
                      headers: { 'ngrok-skip-browser-warning': 'true' } 
                  });
                  if (uploadRes.ok) {
                      const result = await uploadRes.json();
                      if (result.filename || result.url) {
                          signatureUrl = `/public/data/uploads/${detailData[0].Wname || 'Signature'}/${(result.filename || result.url).split('/').pop()}`;
                      }
                  }
              } catch (e) {
                  console.error('上傳簽名失敗:', e);
              }
          }

          const itemsToUpdate = detailData.map(row => ({
              aserno: row.Aserno || row.Mcode,
              sno: row.Sno,
              isCompleted: checkedItems.has(`${row.Aserno || row.Mcode}_${row.Sno}`) ? 1 : 0
          }));

          const res = await fetch(API.UPDATE_REPAIR_STATUS, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
              body: JSON.stringify({ 
                  aserno: detailData[0]?.Aserno || detailData[0]?.Mcode,
                  items: itemsToUpdate,
                  signCust: signatureUrl 
              })
          });

          if (!res.ok) throw new Error(await res.text());
          
          alert('✅ 驗收狀態已更新！');
          setDetailModalOpen(false);
          handleSearch(); 
      } catch (err) {
          handleApiError(err, '更新驗收狀態');
      } finally {
          setSavingStatus(false);
      }
  };

  const renderVendorResults = () => {
      if (results.length === 0 && !loading) {
          return <div className="text-center text-gray-400 py-10 bg-white rounded-xl border border-dashed border-gray-300">查無資料，請設定條件後重新查詢</div>;
      }

      const grouped = results.reduce((acc, item) => {
          const rawMno = item.Mno || item.mno || '';
          const baseMno = rawMno.length >= 11 ? rawMno.substring(0, 11) : rawMno; 
          
          if (!acc[baseMno]) {
              acc[baseMno] = { 
                  ...item, 
                  displayMno: baseMno, 
                  ItemCount: 1, 
                  CompletedCount: (item.IsCompleted === 1 || item.IsCompleted === true) ? 1 : 0 
              };
          } else {
              acc[baseMno].ItemCount += 1;
              acc[baseMno].CompletedCount += (item.IsCompleted === 1 || item.IsCompleted === true) ? 1 : 0;
          }
          return acc;
      }, {});

      const groupedArray = Object.values(grouped).sort((a, b) => {
          const dateA = new Date(a.Sdate || 0).getTime();
          const dateB = new Date(b.Sdate || 0).getTime();
          if(dateB !== dateA) return dateB - dateA; 
          return (b.displayMno || '').localeCompare(a.displayMno || ''); 
      });

      return groupedArray.map((res, idx) => {
          const total = res.ItemCount || 1;
          const completed = res.CompletedCount || 0;
          const isDone = completed === total && total > 0;

          if (hideCompletedVendor && isDone) return null;

          return (
              <div key={idx} className="bg-white border border-gray-200 border-l-4 border-l-orange-500 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow relative">
                  <div className="flex justify-between items-start mb-2">
                      <div className="font-black text-lg text-orange-700">{res.PrdtName || res.prdtName || '未知廠商'}</div>
                      <div className="text-xs bg-orange-100 border border-orange-200 text-orange-800 px-2 py-1 rounded font-mono font-bold shadow-sm">派工單: {res.displayMno}</div>
                  </div>
                  <div className="text-sm text-gray-500 mb-1.5 flex items-center gap-2">
                      <span className="bg-gray-100 px-2 py-0.5 rounded">派修日期: {res.Sdate?.split('T')[0]}</span>
                  </div>
                  <div className="font-bold text-gray-800 text-base">
                      {res.Wname || res.wname} <span className="text-orange-600 font-black">{res.Hcode || res.hcode}</span>
                      <span className="text-gray-600 text-base ml-1 font-medium">等 <span className="font-bold text-blue-700">{res.ItemCount}</span> 筆資料</span>
                  </div>
                  <div className="mt-3 border-t border-gray-100 pt-2 flex justify-between items-center">
                      <div className={`font-bold text-base tracking-widest ${isDone ? 'text-green-600' : 'text-red-500'}`}>
                          {isDone ? '已完成' : '未完成'} ({completed}/{total})
                      </div>
                      <button onClick={() => handleViewDetail(res, 'vendor')} className="text-sm font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1">
                          查看明細 <ChevronRight className="w-4 h-4"/>
                      </button>
                  </div>
              </div>
          );
      });
  };

  const getOverviewData = () => {
      let data = [...ovResults];
      
      if (ovProjects.length > 0) {
          data = data.filter(d => ovProjects.includes(String(d.Wcode || d.wcode)));
      }

      if (ovStatus !== 'all') {
          data = data.filter(d => {
              const isDone = d.IsCompleted === 1 || d.IsCompleted === true;
              return ovStatus === 'done' ? isDone : !isDone;
          });
      }

      data.sort((a, b) => {
          if (ovSort === 'date') {
              return new Date(b.Sdate || 0) - new Date(a.Sdate || 0);
          } else if (ovSort === 'project') {
              const pa = (a.Wname || '') + (a.Hcode || '');
              const pb = (b.Wname || '') + (b.Hcode || '');
              return pa.localeCompare(pb);
          } else if (ovSort === 'vendor') {
              const va = a.PrdtName || a.prdtName || '';
              const vb = b.PrdtName || b.prdtName || '';
              return va.localeCompare(vb);
          } else if (ovSort === 'item') {
              const ia = a.FirstItemName || '';
              const ib = b.FirstItemName || '';
              return ia.localeCompare(ib);
          }
          return 0;
      });

      return data;
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300 print:p-0 print:m-0 print:space-y-0">
      
      <div>
          <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden p-1 mb-4 flex-wrap print:hidden">
            <button onClick={() => setTab('client')} className={`flex-1 min-w-[120px] py-2.5 text-center font-bold rounded-md transition-colors ${tab === 'client' ? 'bg-blue-100 text-blue-700 shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}>
              客戶報修查詢
            </button>
            <button onClick={() => setTab('contractor')} className={`flex-1 min-w-[120px] py-2.5 text-center font-bold rounded-md transition-colors ${tab === 'contractor' ? 'bg-orange-100 text-orange-700 shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}>
              廠商派修查詢
            </button>
            <button onClick={() => setTab('overview')} className={`flex-1 min-w-[120px] py-2.5 text-center font-bold rounded-md transition-colors ${tab === 'overview' ? 'bg-green-100 text-green-700 shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}>
              客戶報修細項一覽表
            </button>
          </div>

          {tab === 'client' && (
            <div className="space-y-3 max-w-3xl mx-auto print:hidden">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-4 space-y-3">
                <div className="flex gap-2">
                    <select className="border border-gray-300 rounded-md p-2.5 bg-gray-50 flex-1 focus:ring-2 focus:ring-blue-500 font-bold text-gray-700" value={searchProj} onChange={e => setSearchProj(e.target.value)}>
                    <option value="">全部建案</option>
                    {globalData.projects.map(p => {
                        const val = String(p.Wcode || p.wcode);
                        const name = p.Wname || p.wname || p.Name || '';
                        return <option key={val} value={val}>{name}</option>
                    })}
                    </select>
                    <button onClick={handleSearch} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-md font-bold shadow flex items-center gap-2 transition-colors">
                    {loading ? <Loader className="w-5 h-5 animate-spin"/> : <Search className="w-5 h-5"/>} 查詢
                    </button>
                </div>
                
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-t border-gray-100 pt-3 gap-3">
                    <div className="grid grid-cols-2 gap-3 w-full md:w-auto">
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">起始日期</label>
                            <input type="date" className="w-full border border-gray-300 rounded-md p-2 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 font-bold text-gray-700" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">結束日期</label>
                            <input type="date" className="w-full border border-gray-300 rounded-md p-2 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 font-bold text-gray-700" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </div>
                    </div>
                    <label className="flex items-center gap-1.5 cursor-pointer bg-blue-50 px-3 py-2 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors">
                        <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" checked={hideCompletedClient} onChange={(e) => setHideCompletedClient(e.target.checked)} />
                        <span className="font-bold text-blue-800 text-sm">已完成者略</span>
                    </label>
                </div>
              </div>

              <div className="space-y-3">
                  {results.length === 0 && !loading && <div className="text-center text-gray-400 py-10 bg-white rounded-xl border border-dashed border-gray-300">查無資料，請設定條件後重新查詢</div>}
                  {results.map((res, idx) => {
                      const total = res.ItemCount || 1;
                      const completed = res.CompletedCount || 0;
                      const isDone = completed === total && total > 0;

                      if (hideCompletedClient && isDone) return null;

                      return (
                          <div key={idx} className="bg-white border border-gray-200 border-l-4 border-l-blue-500 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow relative group">
                            <div className="flex justify-between items-start mb-2">
                                <div className="font-black text-lg text-gray-800">
                                    {res.Wname || res.wname} <span className="text-blue-600 ml-1 font-black">{res.Hcode || res.hcode}</span> <span className="text-gray-600 text-base font-normal ml-1">{res.Name || res.name}</span>
                                </div>
                                <div className="text-xs bg-blue-100 border border-blue-200 text-blue-800 px-2 py-1 rounded font-mono font-bold shadow-sm">單號: {res.Aserno}</div>
                            </div>
                            <div className="text-sm text-gray-500 mb-1.5 flex items-center gap-2">
                                <span className="bg-gray-100 px-2 py-0.5 rounded">報修日期: {res.Sdate?.split('T')[0] || '無日期'}</span> 
                            </div>
                            <div className="font-bold text-gray-800 text-base">
                                <span className="text-sm text-gray-600 bg-blue-50 inline-block px-2 py-0.5 rounded text-blue-800">內容: {res.FirstItemName || '修繕項目'}等 <span className="font-bold">{res.ItemCount || 1}</span> 項</span>
                            </div>
                            <div className="mt-3 border-t border-gray-100 pt-2 flex justify-between items-center">
                                <div className={`font-bold text-base tracking-widest ${isDone ? 'text-green-600' : 'text-red-500'}`}>
                                    {isDone ? '已完成' : '未完成'} ({completed}/{total})
                                </div>
                                <button onClick={() => handleViewDetail(res, 'client')} className="text-sm font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1">
                                    查看明細 <ChevronRight className="w-4 h-4"/>
                                </button>
                            </div>
                          </div>
                      );
                  })}
              </div>
            </div>
          )}

          {tab === 'contractor' && (
            <div className="space-y-3 max-w-3xl mx-auto print:hidden">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-4 space-y-3">
                <div className="flex gap-2">
                    <input type="text" placeholder="輸入廠商名稱或代號關鍵字..." className="border border-gray-300 rounded-md p-2.5 bg-gray-50 flex-1 focus:ring-2 focus:ring-orange-500 font-bold" value={searchKeyword} onChange={e => setSearchKeyword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} />
                    <button onClick={handleSearch} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-md font-bold shadow flex items-center gap-2 transition-colors">
                    {loading ? <Loader className="w-5 h-5 animate-spin"/> : <Search className="w-5 h-5"/>} 查詢
                    </button>
                </div>
                
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-t border-gray-100 pt-3 gap-3">
                    <div className="grid grid-cols-2 gap-3 w-full md:w-auto">
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">起始日期</label>
                            <input type="date" className="w-full border border-gray-300 rounded-md p-2 text-sm bg-gray-50 focus:ring-2 focus:ring-orange-500 font-bold text-gray-700" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">結束日期</label>
                            <input type="date" className="w-full border border-gray-300 rounded-md p-2 text-sm bg-gray-50 focus:ring-2 focus:ring-orange-500 font-bold text-gray-700" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </div>
                    </div>
                    <label className="flex items-center gap-1.5 cursor-pointer bg-orange-50 px-3 py-2 rounded-lg border border-orange-100 hover:bg-orange-100 transition-colors">
                        <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer" checked={hideCompletedVendor} onChange={(e) => setHideCompletedVendor(e.target.checked)} />
                        <span className="font-bold text-orange-800 text-sm">已完成者略</span>
                    </label>
                </div>
              </div>

              <div className="space-y-3">
                  {renderVendorResults()}
              </div>
            </div>
          )}

          {tab === 'overview' && (
              <div className="space-y-4 print:space-y-0">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 print:hidden">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                          <div className="col-span-1 md:col-span-2">
                              <label className="block text-sm font-bold text-gray-700 mb-1">建案別 (可多選)</label>
                              <div className="flex gap-1">
                                  <input 
                                      type="text" readOnly 
                                      className="w-full border border-gray-300 rounded-md p-2 text-sm bg-gray-50 text-green-700 font-bold cursor-pointer hover:bg-gray-100 transition-colors" 
                                      placeholder={ovProjects.length > 0 ? `已選擇 ${ovProjects.length} 個建案` : "全部建案 (請點選右側選擇)"} 
                                      value={ovProjects.length > 0 ? `已選擇 ${ovProjects.length} 個建案` : ''} 
                                      onClick={() => setIsOvProjectModalOpen(true)}
                                  />
                                  <button onClick={() => setIsOvProjectModalOpen(true)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 rounded-md font-bold border border-gray-300 transition-colors">...</button>
                              </div>
                          </div>
                          <div>
                              <label className="block text-sm font-bold text-gray-700 mb-1">起始日期</label>
                              <input type="date" className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-green-500 font-bold" value={startDate} onChange={e => setStartDate(e.target.value)} />
                          </div>
                          <div>
                              <label className="block text-sm font-bold text-gray-700 mb-1">結束日期</label>
                              <input type="date" className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-green-500 font-bold" value={endDate} onChange={e => setEndDate(e.target.value)} />
                          </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-gray-100 pt-4">
                          <div>
                              <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-1"><Filter size={14}/> 完成狀態</label>
                              <select className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-green-500 font-bold" value={ovStatus} onChange={e => setOvStatus(e.target.value)}>
                                  <option value="all">全部狀態</option>
                                  <option value="pending">未完成</option>
                                  <option value="done">已完成</option>
                              </select>
                          </div>
                          <div>
                              <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-1"><ArrowUpDown size={14}/> 排序方式</label>
                              <select className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-green-500 font-bold" value={ovSort} onChange={e => setOvSort(e.target.value)}>
                                  <option value="date">依報修日期</option>
                                  <option value="project">依建案別</option>
                                  <option value="vendor">依廠商別</option>
                                  <option value="item">依修繕項目</option>
                              </select>
                          </div>
                          <div className="flex items-end justify-end gap-2">
                              <button onClick={() => window.print()} className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-bold shadow flex items-center gap-2 transition-colors">
                                  <Printer size={16}/> 列印
                              </button>
                              <button onClick={handleSearch} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-bold shadow flex items-center gap-2 transition-colors">
                                  {loading ? <Loader className="w-5 h-5 animate-spin"/> : <Search className="w-5 h-5"/>} 產生報表
                              </button>
                          </div>
                      </div>
                  </div>

                  <div className="bg-white overflow-hidden printable-area print:p-0 print:border-none print:shadow-none print:w-full print:block print:max-w-none">
                      <div className="text-center mb-2 print:mb-4">
                          <h1 className="text-2xl font-bold tracking-widest text-black">客戶報修細項一覽表</h1>
                          <p className="text-sm text-black">日期區間:{startDate}~{endDate}</p>
                      </div>
                      
                      <div className="overflow-x-auto print:overflow-visible">
                          <table className="w-full text-xs text-center border-collapse border border-black">
                              <thead className="bg-white text-black font-bold">
                                  <tr>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">報修日期</th>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">報修單號</th>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">建案/戶別</th>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">客戶/電話</th>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">報修方式/概要</th>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">修繕類別/原因</th>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">位置/說明</th>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">歸屬原因</th>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">指派廠商/預計完成日</th>
                                      <th className="px-2 py-1.5 border border-black whitespace-nowrap">完成與否</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {ovResults.length === 0 && !loading ? (
                                      <tr><td colSpan="10" className="text-center py-10 border border-black text-gray-500">請設定條件並點擊「產生報表」</td></tr>
                                  ) : (
                                      getOverviewData().length === 0 && !loading ? (
                                          <tr><td colSpan="10" className="text-center py-10 border border-black text-gray-500">篩選條件下查無資料</td></tr>
                                      ) : (
                                          getOverviewData().map((res, idx) => {
                                              const isDone = res.IsCompleted === 1 || res.IsCompleted === true;
                                              
                                              const rawWflg = res.Wflg ?? res.wflg;
                                              const wflgText = getMappedText(rawWflg, WFLG_MAP);
                                              
                                              const rawFlg = res.Flg ?? res.flg;
                                              const flgText = getMappedText(rawFlg, FLG_MAP);
                                              
                                              return (
                                                  <tr key={idx} className="bg-white text-black text-xs h-10">
                                                      <td className="px-1 py-1 border border-black whitespace-nowrap align-top">{res.Sdate?.split('T')[0] || ''}</td>
                                                      <td className="px-1 py-1 border border-black whitespace-nowrap align-top">{res.Aserno}</td>
                                                      <td className="px-2 py-1 border border-black text-left align-top">{res.Wname}<br/>{res.Hcode}</td>
                                                      <td className="px-2 py-1 border border-black text-left align-top">{res.Name}<br/>{res.Tel1 || res.Tel}</td>
                                                      <td className="px-2 py-1 border border-black text-left align-top max-w-[160px]">
                                                          {wflgText !== '無' && <><span className="font-bold">{wflgText}</span><br/></>}
                                                          <span className="text-gray-800">{res.Note1 || ''}</span>
                                                      </td>
                                                      <td className="px-2 py-1 border border-black text-left align-top">
                                                          {res.AcodeName || res.FirstItemName || ''}
                                                          {res.AsnoName ? <><br/>({res.AsnoName})</> : null}
                                                      </td>
                                                      <td className="px-2 py-1 border border-black text-left align-top">
                                                          {res.Note || ''}
                                                          {res.Note2 ? <><br/>({res.Note2})</> : null}
                                                      </td>
                                                      <td className="px-2 py-1 border border-black text-center align-top whitespace-nowrap">{flgText !== '無' ? flgText : ''}</td>
                                                      <td className="px-2 py-1 border border-black text-left align-top">
                                                          {res.PrdtName || ''}
                                                          {res.Date1 ? <><br/>{res.Date1?.split('T')[0]}</> : null}
                                                      </td>
                                                      <td className="px-1 py-1 border border-black text-center align-top font-bold text-sm">
                                                          {isDone ? 'V' : ''}
                                                      </td>
                                                  </tr>
                                              )
                                          })
                                      )
                                  )}
                              </tbody>
                          </table>
                      </div>
                  </div>
              </div>
          )}
      </div>

      {detailModalOpen && (
          <div className="fixed inset-0 bg-gray-500/80 z-[200] flex items-start justify-center overflow-y-auto p-4 sm:p-8 print:static print:bg-transparent print:p-0 print:block print:overflow-visible">
              
              <div id="printable-ticket" className="bg-white w-full max-w-4xl rounded-xl shadow-2xl relative my-auto animate-in fade-in zoom-in-95 duration-200 print:shadow-none print:w-full print:max-w-none print:my-0 print:mx-0 print:animate-none">
                  
                  <div className="bg-gray-800 text-white p-4 rounded-t-xl flex justify-between items-center print:hidden flex-wrap gap-3">
                      <h3 className="font-bold text-lg flex items-center gap-2">
                          {detailType === 'client' ? '客戶報修單明細' : '廠商派工單明細'}
                      </h3>
                      <div className="flex items-center gap-2 md:gap-3 ml-auto">
                          <button onClick={handleSaveStatus} disabled={savingStatus} className="bg-green-600 hover:bg-green-500 px-4 py-2 rounded-lg font-bold shadow flex items-center gap-2 transition-colors disabled:bg-green-400 text-sm md:text-base">
                              {savingStatus ? <Loader className="w-4 h-4 animate-spin"/> : <CheckCircle className="w-4 h-4" />} 
                              儲存狀態
                          </button>
                          <button onClick={() => window.print()} className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg font-bold shadow flex items-center gap-2 transition-colors text-sm md:text-base">
                              <Printer className="w-4 h-4" /> 列印單據
                          </button>
                          <button onClick={() => setDetailModalOpen(false)} className="hover:bg-gray-700 p-1.5 rounded-full transition-colors ml-1">
                              <X className="w-6 h-6"/>
                          </button>
                      </div>
                  </div>

                  <div className="p-4 md:p-8 bg-white text-black min-h-[500px] print:p-0 print:min-h-0">
                      {detailLoading ? (
                          <div className="flex flex-col items-center justify-center h-64 text-gray-500 print:hidden">
                              <Loader className="w-12 h-12 animate-spin mb-4 text-blue-500" />
                              <p className="font-bold text-lg">載入明細資料中...</p>
                          </div>
                      ) : (
                          <>
                              <div className="text-center border-b-2 border-black pb-4 mb-6">
                                  <h1 className="text-3xl font-black tracking-widest mb-2">
                                      {detailType === 'client' ? '客 戶 報 修 單' : '廠 商 派 工 單'}
                                  </h1>
                                  <p className="text-gray-500 text-sm">單據列印時間：{new Date().toLocaleString()}</p>
                              </div>

                              {detailType === 'client' && (detailData.length > 0 || selectedTicket) && (() => {
                                  const masterData = { ...selectedTicket, ...(detailData[0] || {}) };
                                  
                                  const rawWflg = masterData.Wflg ?? masterData.wflg;
                                  const wflgText = getMappedText(rawWflg, WFLG_MAP);

                                  return (
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-6 text-sm md:text-base mb-8 p-5 bg-white border border-gray-300 rounded-xl">
                                          <div><span className="text-gray-500 mr-2">報修單號:</span> <span className="font-bold text-lg">{masterData.Aserno}</span></div>
                                          <div><span className="text-gray-500 mr-2">報修日期:</span> <span className="font-bold">{masterData.Sdate?.split('T')[0]}</span></div>
                                          <div><span className="text-gray-500 mr-2">建案名稱:</span> <span className="font-bold text-blue-800">{masterData.Wname}</span></div>
                                          
                                          <div><span className="text-gray-500 mr-2">戶別:</span> <span className="font-bold text-xl text-blue-800">{masterData.Hcode}</span></div>
                                          <div><span className="text-gray-500 mr-2">客戶姓名:</span> <span className="font-bold">{masterData.Name}</span></div>
                                          <div><span className="text-gray-500 mr-2">聯絡電話:</span> <span className="font-bold">{masterData.Tel1 || masterData.Tel2 || '無'}</span></div>
                                          
                                          <div>
                                              <span className="text-gray-500 mr-2">報修方式:</span> 
                                              <span className="font-bold text-red-600 tracking-widest text-lg">
                                                  {wflgText}
                                              </span>
                                          </div>
                                          <div className="col-span-2 flex items-start">
                                              <span className="text-gray-500 mr-2 flex-shrink-0">報修概要:</span> 
                                              <span className="font-normal">{masterData.Note1 || '無'}</span>
                                          </div>
                                      </div>
                                  );
                              })()}

                              {detailType === 'vendor' && detailData.length > 0 && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6 text-sm md:text-base font-bold mb-8 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                                      <div><span className="text-gray-500 mr-2">派工單號:</span> <span className="font-mono text-lg">{detailData[0].Mno?.substring(0, 11)}</span></div>
                                      <div><span className="text-gray-500 mr-2">派修日期:</span> {detailData[0].Sdate?.split('T')[0]}</div>
                                      <div className="md:col-span-2"><span className="text-gray-500 mr-2">施作廠商:</span> <span className="text-xl text-orange-800">{detailData[0].PrdtName}</span></div>
                                      <div><span className="text-gray-500 mr-2">填單人員:</span> {detailData[0].Bank || '系統'}</div>
                                      <div><span className="text-gray-500 mr-2">派修內容說明:</span> <span className="text-red-700">{detailData[0].DispatchNote || detailData[0].Note || '無'}</span></div>
                                  </div>
                              )}

                              <div className="flex justify-between items-end mb-2">
                                  <h4 className="font-bold text-lg border-l-4 border-black pl-2">修繕明細清單</h4>
                                  <span className="text-sm font-bold text-green-600 print:hidden flex items-center gap-1"><CheckSquare className="w-4 h-4"/> 勾選代表已驗收完成</span>
                              </div>
                              
                              <div className="overflow-x-auto">
                                  <table className="w-full text-sm text-left border-collapse border border-gray-300">
                                      <thead className="bg-gray-100 text-gray-800 font-bold">
                                          {detailType === 'client' ? (
                                              <tr>
                                                  <th className="border border-gray-300 p-2 text-center w-12 print:w-16">
                                                      <input type="checkbox" className="w-4 h-4 rounded text-green-600 cursor-pointer print:hidden" 
                                                          checked={detailData.length > 0 && checkedItems.size === detailData.length}
                                                          onChange={toggleAllChecks} />
                                                      <span className="hidden print:inline">驗收</span>
                                                  </th>
                                                  <th className="border border-gray-300 p-2 text-center w-12">項次</th>
                                                  <th className="border border-gray-300 p-2 text-center w-16 print:hidden">照片</th>
                                                  <th className="border border-gray-300 p-2">修繕類別 / 發生原因</th>
                                                  <th className="border border-gray-300 p-2">位置說明</th>
                                                  <th className="border border-gray-300 p-2 text-center">歸屬原因</th>
                                                  <th className="border border-gray-300 p-2">指派廠商</th>
                                                  <th className="border border-gray-300 p-2 w-28">預計完成日</th>
                                              </tr>
                                          ) : (
                                              <tr>
                                                  <th className="border border-gray-300 p-2 text-center w-12 print:w-16">
                                                      <input type="checkbox" className="w-4 h-4 rounded text-green-600 cursor-pointer print:hidden" 
                                                          checked={detailData.length > 0 && checkedItems.size === detailData.length}
                                                          onChange={toggleAllChecks} />
                                                      <span className="hidden print:inline">驗收</span>
                                                  </th>
                                                  <th className="border border-gray-300 p-2 text-center w-12">項次</th>
                                                  <th className="border border-gray-300 p-2 text-center w-24 print:hidden">照片</th>
                                                  <th className="border border-gray-300 p-2">建案名稱</th>
                                                  <th className="border border-gray-300 p-2">戶別</th>
                                                  <th className="border border-gray-300 p-2">修繕類別 / 項目</th>
                                                  <th className="border border-gray-300 p-2">位置說明</th>
                                                  <th className="border border-gray-300 p-2 w-24">原單號</th>
                                              </tr>
                                          )}
                                      </thead>
                                      <tbody>
                                          {detailData.map((row, index) => {
                                              const uid = `${row.Aserno || row.Mcode}_${row.Sno}`;
                                              const isChecked = checkedItems.has(uid);
                                              
                                              let photos = [];
                                              try {
                                                  if (row.Photo) {
                                                      photos = typeof row.Photo === 'string' ? JSON.parse(row.Photo) : row.Photo;
                                                  }
                                              } catch (e) {
                                                  if (typeof row.Photo === 'string' && row.Photo.trim() !== '') {
                                                      photos = [row.Photo];
                                                  }
                                              }
                                              if (!Array.isArray(photos)) photos = [];
                                              
                                              const rawFlg = row.Flg ?? row.flg;
                                              const flgText = getMappedText(rawFlg, FLG_MAP);

                                              return detailType === 'client' ? (
                                                  <tr key={uid} className={`hover:bg-gray-50 transition-colors ${isChecked ? 'bg-green-50/50' : ''}`}>
                                                      <td className="border border-gray-300 p-2 text-center align-middle">
                                                          <input type="checkbox" className="w-6 h-6 text-green-600 rounded cursor-pointer border-2 border-gray-400 print:appearance-none print:w-5 print:h-5 print:border-black print:rounded-none relative print:checked:after:content-['✔'] print:checked:after:absolute print:checked:after:-top-1 print:checked:after:left-0.5 print:checked:after:text-lg print:checked:after:font-bold print:checked:after:text-black" 
                                                              checked={isChecked} 
                                                              onChange={() => toggleItemCheck(uid)} />
                                                      </td>
                                                      <td className="border border-gray-300 p-2 text-center font-bold align-middle">{row.Sno}</td>
                                                      <td className="border border-gray-300 p-1 align-middle print:hidden">
                                                          {photos.length > 0 ? (
                                                              <div className="flex gap-1 justify-center flex-wrap">
                                                                  {photos.map((src, pIdx) => (
                                                                      <div key={pIdx} className="w-8 h-8 md:w-10 md:h-10 bg-gray-200 rounded overflow-hidden shadow-sm border border-gray-300">
                                                                          <SecureImage 
                                                                              src={resolvePhotoUrl(src)} 
                                                                              className="w-full h-full object-cover cursor-pointer hover:opacity-80" 
                                                                              onClick={() => setViewPhoto({ url: resolvePhotoUrl(src), name: `${row.AcodeName} - ${row.AsnoName}`, note: row.Note })} 
                                                                          />
                                                                      </div>
                                                                  ))}
                                                              </div>
                                                          ) : <div className="text-center text-xs text-gray-400">無</div>}
                                                      </td>
                                                      <td className="border border-gray-300 p-2 font-bold text-blue-800 align-middle">{row.AcodeName} - <span className="text-black font-normal">{row.AsnoName}</span></td>
                                                      <td className="border border-gray-300 p-2 align-middle">{row.Note} {row.Note2 ? `(${row.Note2})` : ''}</td>
                                                      <td className="border border-gray-300 p-2 align-middle text-center font-bold text-red-600 tracking-widest">{flgText !== '無' ? flgText : ''}</td>
                                                      <td className="border border-gray-300 p-2 align-middle">{row.PrdtName || '未派工'}</td>
                                                      <td className="border border-gray-300 p-2 align-middle">{row.Date1?.split('T')[0] || ''}</td>
                                                  </tr>
                                              ) : (
                                                  <tr key={uid} className={`hover:bg-gray-50 transition-colors ${isChecked ? 'bg-green-50/50' : ''}`}>
                                                      <td className="border border-gray-300 p-2 text-center align-middle">
                                                          <input type="checkbox" className="w-6 h-6 text-green-600 rounded cursor-pointer border-2 border-gray-400 print:appearance-none print:w-5 print:h-5 print:border-black print:rounded-none relative print:checked:after:content-['✔'] print:checked:after:absolute print:checked:after:-top-1 print:checked:after:left-0.5 print:checked:after:text-lg print:checked:after:font-bold print:checked:after:text-black" 
                                                              checked={isChecked} 
                                                              onChange={() => toggleItemCheck(uid)} />
                                                      </td>
                                                      <td className="border border-gray-300 p-2 text-center font-bold align-middle">{index + 1}</td>
                                                      <td className="border border-gray-300 p-1 align-middle print:hidden">
                                                          {photos.length > 0 ? (
                                                              <div className="flex gap-1 justify-center flex-wrap">
                                                                  {photos.map((src, pIdx) => (
                                                                      <div key={pIdx} className="w-8 h-8 md:w-10 md:h-10 bg-gray-200 rounded overflow-hidden shadow-sm border border-gray-300">
                                                                          <SecureImage 
                                                                              src={resolvePhotoUrl(src)} 
                                                                              className="w-full h-full object-cover cursor-pointer hover:opacity-80" 
                                                                              onClick={() => setViewPhoto({ url: resolvePhotoUrl(src), name: `${row.AcodeName} - ${row.AsnoName}`, note: row.DetailNote })} 
                                                                          />
                                                                      </div>
                                                                  ))}
                                                              </div>
                                                          ) : <div className="text-center text-xs text-gray-400">無</div>}
                                                      </td>
                                                      <td className="border border-gray-300 p-2 align-middle">{row.Wname}</td>
                                                      <td className="border border-gray-300 p-2 font-bold text-orange-800 align-middle">{row.Hcode}</td>
                                                      <td className="border border-gray-300 p-2 font-bold text-blue-800 align-middle">
                                                          {row.AcodeName ? `${row.AcodeName} - ${row.AsnoName}` : '待查'}
                                                      </td>
                                                      <td className="border border-gray-300 p-2 align-middle">
                                                          {row.DetailNote || '無'} {row.Note2 ? `(${row.Note2})` : ''}
                                                      </td>
                                                      <td className="border border-gray-300 p-2 font-mono text-xs text-gray-500 align-middle">{row.Mcode}</td>
                                                  </tr>
                                              );
                                          })}
                                      </tbody>
                                  </table>
                              </div>

                              <div className="mt-20 grid grid-cols-3 gap-4 md:gap-8 text-center pt-8">
                                  <div className="flex flex-col items-center">
                                      <SignaturePad ref={signatureRef} />
                                      <span className="font-bold text-gray-600 text-sm md:text-base mt-2">客戶簽名 (確認)</span>
                                  </div>
                                  <div className="flex flex-col items-center justify-end">
                                      <div className="border-b border-black w-full mb-2 h-10"></div>
                                      <span className="font-bold text-gray-600 text-sm md:text-base">施作廠商</span>
                                  </div>
                                  <div className="flex flex-col items-center justify-end">
                                      <div className="border-b border-black w-full mb-2 h-10"></div>
                                      <span className="font-bold text-gray-600 text-sm md:text-base">主管簽核</span>
                                  </div>
                              </div>
                          </>
                      )}
                  </div>
              </div>
          </div>
      )}

      {viewPhoto && (
          <div className="fixed inset-0 z-[10000] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200 print:hidden" onClick={() => setViewPhoto(null)}>
              <div className="absolute top-4 right-4 flex gap-4 z-20">
                  <button className="p-2 bg-blue-600/80 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-2 font-bold text-sm shadow-lg border border-blue-400" onClick={(e) => { e.stopPropagation(); window.open(viewPhoto.url, '_blank'); }}>
                      <ExternalLink className="w-4 h-4"/> <span className="hidden md:inline">無法顯示？開啟原圖</span>
                  </button>
                  <button className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); setViewPhoto(null); }}>
                      <X className="w-8 h-8"/>
                  </button>
              </div>
              <div className="w-full max-w-5xl flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                  <SecureImage src={viewPhoto.url} alt="Preview" className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl bg-black" />
                  <div className="bg-gray-900/80 p-4 rounded-xl border border-gray-700 backdrop-blur text-center min-w-[300px] max-w-2xl mt-4">
                      <h3 className="text-xl font-bold text-white mb-1">{viewPhoto.name}</h3>
                      <p className="text-gray-300 text-sm">{viewPhoto.note || "無備註描述"}</p>
                  </div>
              </div>
          </div>
      )}

      <ProjectSelectModal 
          isOpen={isOvProjectModalOpen} 
          onClose={() => setIsOvProjectModalOpen(false)}
          projects={globalData.projects}
          selectedProjects={ovProjects}
          setSelectedProjects={setOvProjects}
      />
    </div>
  );
}
